import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import { useDispatch, useSelector } from "react-redux";
import classes from "./Map.module.css";
import ApiConfigModal from "./ApiConfigModal";
import { buildDtmApiUrl } from "../util/dtmApi";
import { getOverallIdps } from "../store/actions/entityAction";
import { getAdmin1 } from "../store/actions/entityAction";
import { getAdmin2 } from "../store/actions/entityAction";
import { getCauseCategories, matchesSelectedCause } from "../util/causes";

const REGION_COORDS = [
  {
    id: "lac",
    name: "Latin America and the Caribbean",
    lat: -15,
    lng: -60,
    zoom: 4.2,
    countries: [],
  },
  {
    id: "wca",
    name: "West and Central Africa",
    lat: 6,
    lng: 10,
    zoom: 4.6,
    countries: [],
  },
  {
    id: "mena",
    name: "Middle East and North Africa",
    lat: 29,
    lng: 35,
    zoom: 4.8,
    countries: [],
  },
  {
    id: "ehsa",
    name: "East, Horn and Southern Africa",
    lat: -5,
    lng: 35,
    zoom: 4.8,
    countries: [],
  },
  {
    id: "ap",
    name: "Asia and the Pacific",
    lat: 20,
    lng: 120,
    zoom: 4.2,
    countries: [],
  },
  {
    id: "eca",
    name: "Europe and Central Asia",
    lat: 50,
    lng: 45,
    zoom: 4.4,
    countries: [],
  },
];

const DEFAULT_CENTER = { lat: 14, lng: 1 };
const DEFAULT_ZOOM = 3;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const TIMELINE_YEARS = Array.from({ length: 17 }, (_, index) => 2010 + index);
const MAP_STYLE = [
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{ saturation: 36 }, { color: "#333333" }, { lightness: 40 }],
  },
  {
    featureType: "all",
    elementType: "labels.text.stroke",
    stylers: [{ visibility: "on" }, { color: "#ffffff" }, { lightness: 16 }],
  },
  {
    featureType: "all",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative",
    elementType: "geometry.fill",
    stylers: [{ color: "#fefefe" }, { lightness: 20 }],
  },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#fefefe" }, { lightness: 17 }, { weight: 1.2 }],
  },
  {
    featureType: "administrative.province",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.province",
    elementType: "geometry.stroke",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.fill",
    stylers: [{ visibility: "off" }, { weight: "1.00" }],
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{ visibility: "on" }, { color: "#d0d0d0" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }, { lightness: 20 }],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }, { lightness: 21 }],
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#dedede" }, { lightness: 21 }],
  },
  {
    featureType: "poi.park",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }, { lightness: 17 }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#ffffff" }, { lightness: 29 }, { weight: 0.2 }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }, { lightness: 18 }],
  },
  {
    featureType: "road.local",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }, { lightness: 16 }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#f2f2f2" }, { lightness: 19 }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e9e9e9" }, { lightness: 17 }],
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ visibility: "on" }, { color: "#c9dff6" }],
  },
];

const formatValue = (value) => {
    if (!Number.isFinite(value)) return "";
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
        return `${Math.round(value / 1_000_000)}M`;
    }
    if (absValue >= 1_000) {
        return `${Math.round(value / 1_000)}K`;
    }
    return `${Math.round(value)}`;
};

const formatFullValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat("en-US").format(Math.round(number))
    : "";
};

const formatOptionalAdminBubbleValue = (value, dashClassName) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) {
    return `<span class="${dashClassName}">—</span>`;
  }
  return formatFullValue(number);
};

export const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatPercent = (value, total) => {
    if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
        return "0%";
    }
    return `${((value / total) * 100).toFixed(1)}%`;
};

const getItemValue = (item) => item?.value ?? item?.valueMillions ?? 0;

// Returns the figure to display for an item based on the active population
// metric: total (default), male, or female IDPs.
const getItemValueByType = (item, type) => {
  if (type === "male") return Number(item?.maleidps) || 0;
  if (type === "female") return Number(item?.femaleidps) || 0;
  return getItemValue(item);
};

// Reads the numeric round from a data row (the API uses round_number).
const getRowRound = (row) => {
  const value = row?.round_number ?? row?.round ?? row?.Round ?? row?.rounds;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getRowAssessmentDate = (row) =>
  row?.reporting_date ??
  row?.reportingDate ??
  row?.assessment_date ??
  row?.assessmentDate ??
  row?.latest_assessment_date ??
  row?.latestAssessmentDate ??
  row?.date ??
  "";

const chooseLatestAssessmentDate = (current, next) => {
  if (!next) return current || "";
  if (!current) return next;
  const currentTime = new Date(current).getTime();
  const nextTime = new Date(next).getTime();
  if (!Number.isFinite(currentTime)) return next;
  if (!Number.isFinite(nextTime)) return current;
  return nextTime > currentTime ? next : current;
};

const getItemAssessmentDate = (item) => {
  if (!item) return "";
  if (item.assessmentDate) return item.assessmentDate;
  const directDate = getRowAssessmentDate(item);
  if (directDate) return directDate;
  return (item.rows || []).reduce(
    (latestDate, row) =>
      chooseLatestAssessmentDate(latestDate, getRowAssessmentDate(row)),
    "",
  );
};

// Picks which round to aggregate from a set of rows: the explicitly selected
// round when it's present in the data, otherwise the latest available round.
// Returns null when the rows carry no round info (aggregate everything).
const resolveTargetRound = (rows, roundFilter) => {
  const rounds = (rows || [])
    .map(getRowRound)
    .filter((round) => round !== null);
  if (!rounds.length) return null;
  const requested = roundFilter == null ? null : Number(roundFilter);
  // Honor the requested round exactly. If it isn't present in the data, return
  // it anyway so the aggregation matches no rows (→ blank figures + no-data
  // modal) instead of silently falling back to the latest round.
  if (requested != null) return requested;
  return Math.max(...rounds);
};

const normalizeRegionName = (value) =>
  (value || "").trim().toLowerCase().replace(/&/g, "and").replace(/\s+/g, " ");

// Round a value to a "nice" 1 / 2 / 5 × 10ⁿ number, either down (largest nice
// value ≤ v) or up (smallest nice value ≥ v).
const niceRound = (value, direction) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const steps = [1, 2, 5, 10];
  if (direction === "down") {
    let chosen = steps[0];
    for (const step of steps) {
      if (step <= normalized) chosen = step;
    }
    return chosen * magnitude;
  }
  for (const step of steps) {
    if (step >= normalized) return step * magnitude;
  }
  return 10 * magnitude;
};

const getLegendValues = (items, type = "total") => {
  const values = (items || [])
    .map((item) => getItemValueByType(item, type))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (!values.length) {
    return [1_000, 5_000, 10_000, 20_000];
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const top = niceRound(max, "up");
  let bottom = niceRound(min, "down");

  // Collapse-safe: if everything sits in one nice bucket, spread below the top
  // so the legend still shows a meaningful range of sizes.
  if (!Number.isFinite(bottom) || bottom <= 0 || bottom >= top) {
    bottom = top / 8;
  }

  // Geometric (log) spacing from the smallest to the largest on-screen value —
  // proportional symbols span orders of magnitude, so equal ratios read better
  // than equal differences. Endpoints stay exact; middles snap to nice numbers.
  const ratio = Math.pow(top / bottom, 1 / 3);
  const ticks = [0, 1, 2, 3].map((i) => {
    if (i === 0) return bottom;
    if (i === 3) return top;
    return niceRound(bottom * Math.pow(ratio, i), "up");
  });

  return Array.from(new Set(ticks))
    .filter((value) => value > 0)
    .sort((a, b) => a - b);
};

const getMetersPerPixel = (lat, zoom) =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);

const getLatLng = (row) => {
  const lat = Number(
    row?.latitude ??
      row?.lat ??
      row?.Latitude ??
      row?.admin_2_latitude ??
      row?.admin2_latitude ??
      row?.admin_2_lat ??
      row?.admin2_lat ??
      row?.admin_1_latitude ??
      row?.admin1_latitude ??
      row?.admin_1_lat ??
      row?.admin1_lat,
  );
  const lng = Number(
    row?.longitude ??
      row?.lng ??
      row?.lon ??
      row?.Longitude ??
      row?.admin_2_longitude ??
      row?.admin2_longitude ??
      row?.admin_2_lng ??
      row?.admin_2_lon ??
      row?.admin2_lng ??
      row?.admin2_lon ??
      row?.admin_1_longitude ??
      row?.admin1_longitude ??
      row?.admin_1_lng ??
      row?.admin_1_lon ??
      row?.admin1_lng ??
      row?.admin1_lon,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const getOriginLatLng = (row) => {
  const lat = Number(
    row?.idp_origin_admin_1_latitude ??
      row?.idp_origin_admin1_latitude ??
      row?.idp_origin_admin_1_lat ??
      row?.idp_origin_admin1_lat ??
      row?.origin_admin_1_latitude ??
      row?.origin_admin1_latitude ??
      row?.admin_1_origin_latitude ??
      row?.admin1_origin_latitude ??
      row?.origin_admin_1_lat ??
      row?.origin_admin1_lat ??
      row?.admin_1_origin_lat ??
      row?.admin1_origin_lat ??
      row?.origin_latitude ??
      row?.origin_lat,
  );
  const lng = Number(
    row?.idp_origin_admin_1_longitude ??
      row?.idp_origin_admin1_longitude ??
      row?.idp_origin_admin_1_lng ??
      row?.idp_origin_admin_1_lon ??
      row?.idp_origin_admin1_lng ??
      row?.idp_origin_admin1_lon ??
      row?.origin_admin_1_longitude ??
      row?.origin_admin1_longitude ??
      row?.admin_1_origin_longitude ??
      row?.admin1_origin_longitude ??
      row?.origin_admin_1_lng ??
      row?.origin_admin_1_lon ??
      row?.origin_admin1_lng ??
      row?.origin_admin1_lon ??
      row?.admin_1_origin_lng ??
      row?.admin_1_origin_lon ??
      row?.admin1_origin_lng ??
      row?.admin1_origin_lon ??
      row?.origin_longitude ??
      row?.origin_lng ??
      row?.origin_lon,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const getOriginAdmin1Name = (row) =>
  row?.idp_origin_admin_1 ||
  row?.origin_admin_1_name ||
  row?.origin_admin1_name ||
  row?.admin_1_origin_name ||
  row?.admin1_origin_name ||
  row?.origin_admin_1 ||
  row?.origin_admin1 ||
  row?.origin_admin_1_name_en ||
  row?.origin_name ||
  row?.origin;

const getOriginAdmin1Pcode = (row) =>
  row?.idp_origin_admin_1_pcode ||
  row?.origin_admin_1_pcode ||
  row?.origin_admin1_pcode ||
  row?.admin_1_origin_pcode ||
  row?.admin1_origin_pcode ||
  row?.origin_pcode ||
  "";

const hasUsableOriginAdmin1Value = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "not available" &&
    normalized !== "n/a" &&
    normalized !== "na" &&
    normalized !== "null" &&
    normalized !== "undefined"
  );
};

const hasOriginAdmin1Data = (row) =>
  hasUsableOriginAdmin1Value(row?.idp_origin_admin_1) ||
  hasUsableOriginAdmin1Value(row?.idp_origin_admin_1_pcode);

const getDestinationAdmin1Name = (row) =>
  row?.admin_1_name ||
  row?.admin1_name ||
  row?.admin_1 ||
  row?.admin1 ||
  row?.admin_1_name_en;

const getAdmin1Key = (admin1) =>
  String(admin1?.admin1_pcode || admin1?.id || normalizeRegionName(admin1?.name || ""));
const getHighlightedAdmin1Key = (highlightedAdmin1) =>
  String(
    highlightedAdmin1?.admin1_pcode ||
      highlightedAdmin1?.id ||
      normalizeRegionName(highlightedAdmin1?.name || ""),
  );
const getHighlightedAdmin1Keys = (highlightedAdmin1) =>
  Array.from(
    new Set(
      [
        highlightedAdmin1?.admin1_pcode,
        highlightedAdmin1?.id,
        normalizeRegionName(highlightedAdmin1?.name || ""),
      ]
        .filter(Boolean)
        .map((value) => String(value)),
    ),
  );

const ADMIN1_DEFAULT_COLOR = "#0f8b36";
const ADMIN1_MOVEMENT_INACTIVE_COLOR = "#9aa4b2";
const MOVEMENT_COLORS = {
  arrivals: {
    base: "#418fde",
    hover: "#1f5fbf",
    fillOpacity: 0.18,
    strokeOpacity: 0.75,
  },
  departing: {
    base: "#ea580c",
    hover: "#c2410c",
    fillOpacity: 0.2,
    strokeOpacity: 0.82,
  },
};
const ADMIN1_POLYGON_DEFAULT = {
  fillColor: "#aeb8cc",
  fillOpacity: 0.2,
  strokeColor: "#9aa7c8",
  strokeOpacity: 0.6,
};
const ADMIN1_POLYGON_SELECTED = {
  fillColor: "#0f8b36",
  fillOpacity: 0.18,
  strokeColor: "#0f8b36",
  strokeOpacity: 0.8,
};
const getMovementColors = (direction) =>
  direction === "departing" ? MOVEMENT_COLORS.departing : MOVEMENT_COLORS.arrivals;
const getMovementRelatedPolygonStyle = (direction) => {
  const colors = getMovementColors(direction);
  return {
    fillColor: colors.base,
    fillOpacity: colors.fillOpacity,
    strokeColor: colors.base,
    strokeOpacity: colors.strokeOpacity,
  };
};

const getMovementDetail = ({ link, direction, selectedName }) => ({
  direction,
  from: direction === "departing" ? selectedName : link.name,
  to: direction === "departing" ? link.name : selectedName,
  value: Number(link.value) || 0,
  isInternal: Boolean(link.isInternal),
});

const toFiniteNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const SYMBOL_MAX_RADIUS_PX = 24;
const SYMBOL_MIN_RADIUS_PX = 7;

// Logarithmic symbol scaling: radius is interpolated against log(value) between
// the smallest and largest legend ticks, so each order-of-magnitude step grows
// by an even amount. This keeps small values distinguishable when the data spans
// several orders of magnitude (e.g. 200K vs 1M vs 10M). maxValue/minValue are the
// largest/smallest legend ticks — the same reference used to draw the legend
// bubbles — so a map circle and a legend bubble of the same value match.
const getSymbolRadiusPx = (value, maxValue, minValue) => {
  const v = Number.isFinite(value) ? value : 0;
  const max = Number.isFinite(maxValue) ? maxValue : 0;
  if (v <= 0 || max <= 0) return 0;
  // Log floor: use the smallest legend tick, falling back to two orders of
  // magnitude below the max so we always have a valid range.
  let min = Number.isFinite(minValue) && minValue > 0 ? minValue : max / 100;
  if (min >= max) min = max / 100;
  const clamped = Math.min(Math.max(v, min), max);
  const t =
    (Math.log(clamped) - Math.log(min)) / (Math.log(max) - Math.log(min));
  return (
    SYMBOL_MIN_RADIUS_PX + (SYMBOL_MAX_RADIUS_PX - SYMBOL_MIN_RADIUS_PX) * t
  );
};

// eslint-disable-next-line no-unused-vars
const buildAdm0BoundaryUrl = (iso3) =>
  `https://gisportal.iom.int/arcgis/rest/services/REF/GlobalDTM_ADM0_SIM/MapServer/0/query?where=ISO3%3D%27${encodeURIComponent(
    iso3,
  )}%27&text=&objectIds=&time=&timeRelation=esriTimeRelationOverlaps&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&distance=&units=esriSRUnit_Foot&relationParam=&outFields=&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&havingClause=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&returnExtentOnly=false&sqlFormat=none&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=pjson`;

const buildAdm1BoundaryUrl = (iso3) =>
  `https://gisportal.iom.int/arcgis/rest/services/REF/Global_DTM_ADM1_SIM/MapServer/0/query?where=ISO3%3D%27${encodeURIComponent(
    iso3,
  )}%27&text=&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=ADM1_CODE%2CADM1_NAME%2CCOUNTRY&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&having=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&queryByDistance=&returnExtentOnly=false&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=pjson`;

const buildAdm2BoundaryUrl = (iso3) =>
  `https://gisportal.iom.int/arcgis/rest/services/REF/Global_DTM_ADM2/MapServer/0/query?where=ISO3%3D%27${encodeURIComponent(
    iso3,
  )}%27&text=&objectIds=&time=&timeRelation=esriTimeRelationOverlaps&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&distance=&units=esriSRUnit_Foot&relationParam=&outFields=*&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&havingClause=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&returnExtentOnly=false&sqlFormat=none&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=pjson`;

// eslint-disable-next-line no-unused-vars
const createAdm0BoundaryPolygons = (maps, map, features = []) =>
  features.flatMap((feature) => {
    const rings = feature?.geometry?.rings || [];
    if (!rings.length) return [];
    const paths = rings.map((ring) =>
      ring.map(([lng, lat]) => ({ lat, lng })),
    );
    const polygon = new maps.Polygon({
      paths,
      strokeColor: "#9aa7c8",
      strokeOpacity: 0.6,
      strokeWeight: 1,
      fillColor: "#aeb8cc",
      fillOpacity: 0.2,
      clickable: false,
    });
    polygon.setMap(map);
    return [polygon];
  });

const createAdm1BoundaryPolygons = (
  maps,
  map,
  features = [],
  shadedCodes = new Set(),
  shadedNames = new Set(),
  admin1ByCode = new Map(),
  admin1ByName = new Map(),
  admin1PolygonRefs,
  onAdmin1Click,
  onAdmin1Hover,
) =>
  features.flatMap((feature) => {
    const rings = feature?.geometry?.rings || [];
    if (!rings.length) return [];
    const code = feature?.attributes?.ADM1_CODE;
    const name = feature?.attributes?.ADM1_NAME;
    const normalizedName = normalizeRegionName(name);
    const shouldShade =
      (code && shadedCodes.has(String(code))) ||
      (normalizedName && shadedNames.has(normalizedName));
    if (!shouldShade) return [];
    const admin1 =
      (code && admin1ByCode.get(String(code))) ||
      (normalizedName && admin1ByName.get(normalizedName));
    const admin1Key = admin1 ? getAdmin1Key(admin1) : "";
    const paths = rings.map((ring) =>
      ring.map(([lng, lat]) => ({ lat, lng })),
    );
    const polygon = new maps.Polygon({
      paths,
      strokeColor: "#9aa7c8",
      strokeOpacity: 0.6,
      strokeWeight: 1,
      fillColor: "#aeb8cc",
      fillOpacity: 0.2,
      clickable: Boolean(admin1),
      zIndex: 1,
    });
    if (admin1 && admin1PolygonRefs) {
      const polygons = admin1PolygonRefs.current.get(admin1Key) || [];
      polygons.push(polygon);
      admin1PolygonRefs.current.set(admin1Key, polygons);
    }
    if (admin1) {
      polygon.addListener("mouseover", () => {
        onAdmin1Hover?.(admin1Key, true);
      });
      polygon.addListener("mouseout", () => {
        onAdmin1Hover?.(admin1Key, false);
      });
      polygon.addListener("click", (event) => {
        event?.stop?.();
        onAdmin1Click?.(admin1);
      });
    }
    polygon.setMap(map);
    return [polygon];
  });

// Default (grey) vs selected (green) styling for admin 2 boundary polygons, so a
// selected admin 2 is highlighted by its boundary instead of recoloring the bubble.
const ADMIN2_POLYGON_DEFAULT = {
  strokeColor: "#9aa7c8",
  strokeOpacity: 0.6,
  strokeWeight: 1,
  fillColor: "#aeb8cc",
  fillOpacity: 0.2,
};
const ADMIN2_POLYGON_SELECTED = {
  strokeColor: "#0f8b36",
  strokeOpacity: 0.95,
  strokeWeight: 2.5,
  fillColor: "#0f8b36",
  fillOpacity: 0.22,
};

const createAdm2BoundaryPolygons = (
  maps,
  map,
  features = [],
  shadedCodes = new Set(),
  shadedNames = new Set(),
) =>
  features.flatMap((feature) => {
    const rings = feature?.geometry?.rings || [];
    if (!rings.length) return [];
    const code = feature?.attributes?.ADM2_CODE || feature?.attributes?.ADM2_PCODE;
    const name = feature?.attributes?.ADM2_NAME;
    const normalizedName = normalizeRegionName(name);
    const shouldShade =
      (code && shadedCodes.has(String(code))) ||
      (normalizedName && shadedNames.has(normalizedName));
    if (!shouldShade) return [];
    const paths = rings.map((ring) =>
      ring.map(([lng, lat]) => ({ lat, lng })),
    );
    const polygon = new maps.Polygon({
      paths,
      ...ADMIN2_POLYGON_DEFAULT,
      clickable: false,
    });
    // Tag so the bubble click can find and highlight this admin 2's boundary.
    polygon.__isAdmin2Boundary = true;
    polygon.__admin2Code = code ? String(code) : "";
    polygon.__admin2Name = normalizedName;
    polygon.setMap(map);
    return [polygon];
  });

// Build a LatLngBounds from boundary feature geometry (optionally filtered),
// so the map can be fit to the full extent of the boundary rather than the
// (operation-filtered) data points.
const computeFeatureBounds = (maps, features, predicate) => {
  if (!maps || !Array.isArray(features)) return null;
  const bounds = new maps.LatLngBounds();
  let hasPoint = false;
  features.forEach((feature) => {
    if (predicate && !predicate(feature)) return;
    (feature?.geometry?.rings || []).forEach((ring) => {
      ring.forEach(([lng, lat]) => {
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          bounds.extend(new maps.LatLng(lat, lng));
          hasPoint = true;
        }
      });
    });
  });
  return hasPoint ? bounds : null;
};

const loadGoogleMaps = (apiKey) =>
  new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }

    if (!apiKey) {
      reject(new Error("missing-key"));
      return;
    }

    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", () =>
        resolve(window.google.maps),
      );
      existingScript.addEventListener("error", () =>
        reject(new Error("load-failed")),
      );
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("load-failed"));
    document.head.appendChild(script);
  });

const createLabelOverlay = (
  maps,
  map,
  { position, offset, className, html, value, onClick },
) => {
  const overlay = new maps.OverlayView();
  overlay.isLabel = true;
  overlay.priority = Number.isFinite(value) ? value : 0;
  overlay.decluttered = false;
  overlay.forceVisible = false;
  overlay.onAdd = function onAdd() {
    this.div = document.createElement("div");
    this.div.className = className;
    this.div.innerHTML = html;
    if (onClick) {
      this.div.style.pointerEvents = "auto";
      this.div.style.cursor = "pointer";
      ["mousedown", "mouseup", "pointerdown", "pointerup", "dblclick"].forEach(
        (eventName) => {
          this.div.addEventListener(eventName, (event) => {
            event.stopPropagation();
          });
        },
      );
      this.div.addEventListener("click", onClick);
    }
    const panes = this.getPanes();
    panes.overlayMouseTarget.appendChild(this.div);
  };
  overlay.draw = function draw() {
    if (!this.div) return;
    const projection = this.getProjection();
    if (!projection) return;
    const point = projection.fromLatLngToDivPixel(
      new maps.LatLng(position.lat, position.lng),
    );
    const resolvedOffset =
      typeof offset === "function" ? offset({ map, position, point }) : offset;
    const offsetX = resolvedOffset?.x ?? 0;
    const offsetY = resolvedOffset?.y ?? 0;
    if (resolvedOffset?.placement) {
      this.div.dataset.placement = resolvedOffset.placement;
    }
    this.div.style.left = `${point.x + offsetX}px`;
    this.div.style.top = `${point.y + offsetY}px`;
  };
  overlay.onRemove = function onRemove() {
    if (this.div) {
      this.div.remove();
      this.div = null;
    }
  };
  overlay.setMap(map);
  return overlay;
};

const createMovementMapOverlays = (
  maps,
  map,
  { links = [], internalMovement = null, direction, selectedName, onHoverDetail },
) => {
  const movementColors = getMovementColors(direction);
  const allMovementValues = [
    ...links.map((link) => Number(link.value) || 0),
    Number(internalMovement?.value) || 0,
  ];
  const linkValues = allMovementValues.filter((value) => value > 0);
  const maxLinkValue = linkValues.length ? Math.max(...linkValues) : 0;
  const minLinkValue = linkValues.length ? Math.min(...linkValues) : 0;
  const getMidpoint = (from, to) => ({
    lat: (from.lat + to.lat) / 2,
    lng: (from.lng + to.lng) / 2,
  });
  const getBearing = (from, to) => {
    const fromLat = (from.lat * Math.PI) / 180;
    const toLat = (to.lat * Math.PI) / 180;
    const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;
    const y = Math.sin(deltaLng) * Math.cos(toLat);
    const x =
      Math.cos(fromLat) * Math.sin(toLat) -
      Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLng);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  };
  const getArrowIcon = (color, scale = 2) => ({
    path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
    scale,
    rotation: 0,
    strokeColor: color,
    strokeOpacity: 1,
    strokeWeight: 1.5,
    fillColor: color,
    fillOpacity: 1,
  });

  const overlays = links.flatMap((link) => {
    const from = {
      lat: Number(link.from.lat),
      lng: Number(link.from.lng),
    };
    const to = {
      lat: Number(link.to.lat),
      lng: Number(link.to.lng),
    };
    if (
      !Number.isFinite(from.lat) ||
      !Number.isFinite(from.lng) ||
      !Number.isFinite(to.lat) ||
      !Number.isFinite(to.lng)
    ) {
      console.warn("[IDP movement] Skipping link with invalid coordinates", {
        link,
        from,
        to,
      });
      return [];
    }

    const path = [
      new maps.LatLng(from.lat, from.lng),
      new maps.LatLng(to.lat, to.lng),
    ];
    const arrowPosition = getMidpoint(from, to);
    const arrowRotation = getBearing(from, to);
    const visibleLine = new maps.Polyline({
      path,
      geodesic: true,
      strokeColor: movementColors.base,
      strokeOpacity: 1,
      strokeWeight: 1,
      zIndex: 999999,
      clickable: false,
      visible: true,
      map,
    });
    const hitLine = new maps.Polyline({
      path,
      geodesic: true,
      strokeColor: movementColors.base,
      strokeOpacity: 0.01,
      strokeWeight: 18,
      zIndex: 1000000,
      clickable: true,
      visible: true,
      map,
    });
    const arrowMarker = new maps.Marker({
      position: arrowPosition,
      clickable: false,
      zIndex: 1000002,
      optimized: false,
      icon: {
        ...getArrowIcon(movementColors.base),
        rotation: arrowRotation,
      },
      map,
    });
    const targetPoint = direction === "departing" ? to : from;
    const contributionCircle = new maps.Circle({
      center: targetPoint,
      radius:
        getSymbolRadiusPx(Number(link.value) || 0, maxLinkValue, minLinkValue) *
        getMetersPerPixel(targetPoint.lat, map.getZoom() ?? DEFAULT_ZOOM),
      fillColor: movementColors.base,
      fillOpacity: 0.9,
      strokeColor: "#ffffff",
      strokeOpacity: 1,
      strokeWeight: 2,
      clickable: true,
      zIndex: 1000001,
      visible: true,
      map,
    });
    contributionCircle.__radiusPx = getSymbolRadiusPx(
      Number(link.value) || 0,
      maxLinkValue,
      minLinkValue,
    );
    contributionCircle.__lat = targetPoint.lat;

    const movementDetail = getMovementDetail({ link, direction, selectedName });
    const setHover = (isHovered, showDetail = false) => {
      visibleLine.setOptions({
        strokeColor: isHovered ? movementColors.hover : movementColors.base,
        strokeWeight: isHovered ? 3 : 1,
      });
      arrowMarker.setIcon({
        ...getArrowIcon(
          isHovered ? movementColors.hover : movementColors.base,
          isHovered ? 2.4 : 2.2,
        ),
        rotation: arrowRotation,
      });
      contributionCircle.setOptions({
        fillOpacity: isHovered ? 1 : 0.9,
        strokeColor: isHovered ? movementColors.hover : "#ffffff",
        strokeWeight: isHovered ? 3 : 2,
      });
      if (showDetail) {
        onHoverDetail?.(isHovered ? movementDetail : null);
      }
    };

    hitLine.addListener("mouseover", () => setHover(true, true));
    hitLine.addListener("mouseout", () => setHover(false, true));
    contributionCircle.addListener("mouseover", () => setHover(true, true));
    contributionCircle.addListener("mouseout", () => setHover(false, true));

    return [visibleLine, hitLine, arrowMarker, contributionCircle];
  });

  if (
    internalMovement?.value > 0 &&
    Number.isFinite(Number(internalMovement.lat)) &&
    Number.isFinite(Number(internalMovement.lng))
  ) {
    const position = {
      lat: Number(internalMovement.lat),
      lng: Number(internalMovement.lng),
    };
    const radiusPx = Math.max(
      getSymbolRadiusPx(internalMovement.value, maxLinkValue, minLinkValue),
      14,
    );
    const internalCircle = new maps.Circle({
      center: position,
      radius: radiusPx * getMetersPerPixel(position.lat, map.getZoom() ?? DEFAULT_ZOOM),
      fillColor: movementColors.base,
      fillOpacity: 0.95,
      strokeColor: "#ffffff",
      strokeOpacity: 1,
      strokeWeight: 3,
      clickable: true,
      zIndex: 1000003,
      visible: true,
      map,
    });
    internalCircle.__radiusPx = radiusPx;
    internalCircle.__lat = position.lat;

    const internalIcon = new maps.Marker({
      position,
      clickable: false,
      zIndex: 1000004,
      optimized: false,
      icon: {
        path: maps.SymbolPath.CIRCLE,
        scale: 0,
        fillOpacity: 0,
        strokeOpacity: 0,
      },
      label: {
        text: "\u21bb",
        color: "#ffffff",
        fontSize: "20px",
        fontWeight: "900",
      },
      map,
    });

    const internalDetail = {
      direction,
      from: selectedName,
      to: selectedName,
      value: internalMovement.value,
      isInternal: true,
    };
    const setInternalHover = (isHovered) => {
      internalCircle.setOptions({
        fillColor: isHovered ? movementColors.hover : movementColors.base,
        fillOpacity: 1,
        strokeColor: isHovered ? movementColors.hover : "#ffffff",
        strokeWeight: isHovered ? 4 : 3,
      });
      onHoverDetail?.(isHovered ? internalDetail : null);
    };
    internalCircle.addListener("mouseover", () => setInternalHover(true));
    internalCircle.addListener("mouseout", () => setInternalHover(false));

    overlays.push(internalCircle, internalIcon);
  }

  return overlays;
};

const setOverlayVisibility = (overlay, isVisible, visibleClass) => {
  if (!overlay?.div) return;
  overlay.div.classList.toggle(visibleClass, isVisible);
};

const rectsOverlap = (a, b, pad = 2) =>
  a.left < b.right + pad &&
  a.right + pad > b.left &&
  a.top < b.bottom + pad &&
  a.bottom + pad > b.top;

// Greedy label decluttering: keep the highest-value labels, hide any whose box
// collides with one already kept. Hidden labels still reveal on hover. Runs on
// every map idle (zoom/pan) so the kept set re-spreads as space opens up.
const declutterLabels = (labels) => {
  const placed = [];
  const sorted = [...(labels || [])]
    .filter((label) => label && label.div)
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  sorted.forEach((label) => {
    if (label.forceVisible) {
      label.div.style.visibility = "visible";
      label.decluttered = false;
      placed.push(label.div.getBoundingClientRect());
      return;
    }
    const rect = label.div.getBoundingClientRect();
    const collides = placed.some((other) => rectsOverlap(other, rect));
    if (collides) {
      label.div.style.visibility = "hidden";
      label.decluttered = true;
    } else {
      label.div.style.visibility = "visible";
      label.decluttered = false;
      placed.push(rect);
    }
  });
};

// Let a circle reveal its label on hover even when decluttering has hidden it.
const attachLabelReveal = (circle, labelOverlay) => {
  circle.addListener("mouseover", () => {
    labelOverlay.forceVisible = true;
    if (labelOverlay.div) {
      labelOverlay.div.style.visibility = "visible";
      labelOverlay.div.style.zIndex = "5";
    }
  });
  circle.addListener("mouseout", () => {
    labelOverlay.forceVisible = false;
    if (labelOverlay.div) {
      labelOverlay.div.style.zIndex = "";
      labelOverlay.div.style.visibility = labelOverlay.decluttered
        ? "hidden"
        : "visible";
    }
  });
};



const MapView = () => {
  const [activeRegionId, setActiveRegionId] = useState(null);
  const [mapStatus, setMapStatus] = useState("idle");
  const [apiUrlModalOpen, setApiUrlModalOpen] = useState(false);
  const [infoCollapsed, setInfoCollapsed] = useState(false);
  const [movementAdmin1, setMovementAdmin1] = useState(null);
  const [movementDirection, setMovementDirection] = useState("arrivals");
  const [movementHoverDetail, setMovementHoverDetail] = useState(null);
  const [mapToast, setMapToast] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapsRef = useRef(null);
  const mapToastTimerRef = useRef(null);
  const boundaryPolygonsRef = useRef([]);
  const admin1InfoOverlayRef = useRef(null);
  const openAdmin1InfoOverlayRef = useRef(null);
  const applyHighlightedAdmin1Ref = useRef(null);
  const admin1PolygonHoverRef = useRef(null);
  const movementAdmin1Ref = useRef(null);
  const movementDirectionRef = useRef("arrivals");
  const movementLinkDetailsRef = useRef(new Map());
  const movementPolylinesRef = useRef([]);
  const movementContributionCirclesRef = useRef([]);
  const admin1CircleRefs = useRef(new Map());
  const admin1LabelRefs = useRef(new Map());
  const admin1PolygonRefs = useRef(new Map());
  const admin1MovementPolygonStylesRef = useRef(new Map());
  const overlaysRef = useRef({
    regions: [],
    countries: [],
    admin1: [],
    admin2: [],
  });
  const activeLabelsRef = useRef([]);
  const selectedRegion = useSelector((state) => state.selection.region);
  const selectedCountry = useSelector((state) => state.selection.country);
  const selectedCountryPcode = useSelector(
    (state) => state.selection.countrypcode,
  );
  const selectedAdmin1 = useSelector((state) => state.selection.admin1);
  const highlightedAdmin1 = useSelector(
    (state) => state.selection.highlightedAdmin1,
  );
  const selectedAdmin2 = useSelector((state) => state.selection.admin2);
  const selectedAdmin1Pcode = useSelector((state) => state.selection.admin1pcode);
  const selectedCause = useSelector((state) => state.selection.cause);
  const selectedYear = useSelector((state) => state.selection.year);
  const mapLoading = useSelector((state) => state.selection.mapLoading);
  const mapLoadingRef = useRef(false);
  const mapLoadingStartedAtRef = useRef(0);
  const regionsToUse = useSelector((state) => state.agg.regional_Agg);
  const allCountriesList = useSelector((state) => state.agg.country_Agg);
  const admin1Data = useSelector((state) => state.agg.admin1);
  const admin2Data = useSelector((state) => state.agg.admin2);
  const operationList = useSelector((state) => state.agg.operation_List);
  const year = useSelector((state) => state.selection.year);
  const selectedOperation = useSelector((state) => state.selection.operation);
  const selectedRoundFilter = useSelector(
    (state) => state.selection.roundFilter,
  );
  const populationType = useSelector((state) => state.selection.populationType);
  const storedMovementDirection = useSelector(
    (state) => state.selection.idpMovementDirection || "arrivals",
  );
  const getDisplayValue = useCallback(
    (item) => getItemValueByType(item, populationType),
    [populationType],
  );
  const dataurl = `https://dtmapi.iom.int/api/idpAdmin0Data/GetAdmin0Datav2?CountryName=&Operation=&FromReportingDate=2010-01-01&ToReportingDate=2026-02-03&FromRoundNumber=&ToRoundNumber=&Pcode=${selectedCountryPcode}&DataSource=&ResponseFormat=JSON`

  // DTM API v3 configuration URL for the current drill-down level. The endpoint
  // and pcode parameter follow the active admin level, with the current
  // selections (year, operation, cause) appended when available.
  const dtmApiUrl = useMemo(
    () =>
      buildDtmApiUrl({
        countryPcode: selectedCountryPcode,
        admin1Pcode: selectedAdmin1 ? selectedAdmin1Pcode : "",
        year: selectedYear,
        operation: selectedOperation,
        cause: selectedCause,
      }),
    [
      selectedAdmin1,
      selectedAdmin1Pcode,
      selectedCountryPcode,
      selectedYear,
      selectedOperation,
      selectedCause,
    ],
  );

  const regionMetaByName = useMemo(() => {
    const map = new Map();
    regionsToUse.forEach((row) => {
      const key = normalizeRegionName(row.region);
      if (!key) return;
      const entry = map.get(key) ?? { years: new Set(), causes: new Set() };
      if (Number.isFinite(row.year)) entry.years.add(row.year);
      getCauseCategories(row.reason_for_displacement).forEach((cause) =>
        entry.causes.add(cause),
      );
      map.set(key, entry);
    });
    return map;
  }, [regionsToUse]);

  // The regional aggregate doesn't carry a sex breakdown, so derive region-level
  // male/female by summing the per-country figures (country_Agg has both, plus a
  // region field) under the same year/cause filters.
  const regionGenderByName = useMemo(() => {
    const map = new Map();
    const selectedYearNumber = Number(selectedYear);
    const hasSelectedYear = Number.isFinite(selectedYearNumber);
    (allCountriesList || []).forEach((row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
      if (!matchesSelectedCause(row.reason_for_displacement, selectedCause))
        return;
      const key = normalizeRegionName(
        row.region || row.region_name || row.regionName,
      );
      if (!key) return;
      const entry = map.get(key) ?? { maleidps: 0, femaleidps: 0 };
      entry.maleidps += Number.isFinite(row.male) ? row.male : 0;
      entry.femaleidps += Number.isFinite(row.female) ? row.female : 0;
      map.set(key, entry);
    });
    return map;
  }, [allCountriesList, selectedCause, selectedYear]);

  const derivedRegions = useMemo(() => {
    const selectedYearNumber = Number(selectedYear);
    const hasSelectedYear = Number.isFinite(selectedYearNumber);
    if (!regionsToUse?.length) {
      return REGION_COORDS.map((baseRegion) => ({
        ...baseRegion,
        value: getItemValue(baseRegion),
        years: baseRegion.years ?? [],
        causes: baseRegion.causes ?? [],
      }));
    }
    const aggMap = new Map();
    regionsToUse.forEach((row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
      if (!matchesSelectedCause(row.reason_for_displacement, selectedCause))
        return;
      const key = normalizeRegionName(row.region);
      if (!key) return;
      const current = aggMap.get(key) ?? 0;
      const idpsValue = Number.isFinite(row.idps) ? row.idps : 0;
      aggMap.set(key, current + idpsValue);
    });
    const total = Array.from(aggMap.values()).reduce(
      (sum, value) => sum + value,
      0,
    );
    return REGION_COORDS.map((baseRegion) => {
      const key = normalizeRegionName(baseRegion.name);
      const value = aggMap.get(key) ?? 0;
      if (!value) return null;
      const meta = regionMetaByName.get(key);
      const years = meta ? Array.from(meta.years).sort((a, b) => a - b) : [];
      const causes = meta ? Array.from(meta.causes) : [];
      const percent = formatPercent(value, total);
      const gender = regionGenderByName.get(key) ?? {
        maleidps: 0,
        femaleidps: 0,
      };
      return {
        ...baseRegion,
        value,
        maleidps: gender.maleidps,
        femaleidps: gender.femaleidps,
        years,
        causes,
        percent,
      };
    }).filter(Boolean);
  }, [regionGenderByName, regionMetaByName, regionsToUse, selectedCause, selectedYear]);

  // Build the operation list from the global, unfiltered operation_List so the
  // menu isn't shrunk by the current operation selection. Rows are
  // { operation, admin_0_pcode }; scope them to the selected country.
  const operationOptions = useMemo(() => {
    const operations = new Set();
    (Array.isArray(operationList) ? operationList : []).forEach((row) => {
      const operation = row.operation || row.Operation || row.operation_name;
      if (!operation) return;
      const pcode = row.admin_0_pcode || row.admin0_pcode || row.adm0_0_pcode;
      if (!selectedCountryPcode || pcode === selectedCountryPcode) {
        operations.add(operation);
      }
    });
    return ["All", ...Array.from(operations).sort()];
  }, [operationList, selectedCountryPcode]);

  // Operations that actually have data at the current level/year. Since the
  // admin data is now fetched with all operations, the distinct operations in it
  // tell us which dropdown options to enable. `null` = no gating (data not
  // loaded). Source follows the level: admin2 rows at admin1 level, else admin1.
  const operationsWithData = useMemo(() => {
    const source = selectedAdmin1 ? admin2Data : selectedCountry ? admin1Data : null;
    if (!Array.isArray(source) || !source.length) return null;
    const yearNumber = Number(selectedYear);
    const hasYear = Number.isFinite(yearNumber);
    const available = new Set();
    source.forEach((row) => {
      if (hasYear && Number(row.year) !== yearNumber) return;
      const idps = Number(row.idps);
      if (!Number.isFinite(idps) || idps <= 0) return;
      const operation = row.operation || row.Operation || row.operation_name;
      if (operation) available.add(operation);
    });
    return available.size ? available : null;
  }, [selectedAdmin1, selectedCountry, admin1Data, admin2Data, selectedYear]);

  const isOperationDisabled = (operation) => {
    // "All" and the current selection are always selectable; only gate when we
    // have a dataset to judge against.
    if (
      operation === "All" ||
      operation === selectedOperation ||
      !operationsWithData
    ) {
      return false;
    }
    return !operationsWithData.has(operation);
  };

  const activeRegion = useMemo(
    () =>
      derivedRegions.find((region) => region.id === activeRegionId) ||
      REGION_COORDS.find((region) => region.id === activeRegionId) ||
      null,
    [activeRegionId, derivedRegions],
  );

  const dispatch = useDispatch();

  const showMapToast = useCallback((message) => {
    if (mapToastTimerRef.current) {
      window.clearTimeout(mapToastTimerRef.current);
    }
    setMapToast(message);
    mapToastTimerRef.current = window.setTimeout(() => {
      setMapToast(null);
      mapToastTimerRef.current = null;
    }, 3200);
  }, []);

  useEffect(() => {
    movementAdmin1Ref.current = movementAdmin1;
  }, [movementAdmin1]);

  useEffect(() => {
    movementDirectionRef.current = movementDirection;
  }, [movementDirection]);

  useEffect(() => {
    if (storedMovementDirection !== movementDirection) {
      setMovementDirection(storedMovementDirection);
    }
  }, [movementDirection, storedMovementDirection]);

  useEffect(
    () => () => {
      if (mapToastTimerRef.current) {
        window.clearTimeout(mapToastTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    mapLoadingRef.current = mapLoading;
    if (mapLoading) {
      mapLoadingStartedAtRef.current = Date.now();
    }
  }, [mapLoading]);

  const startMapLoading = useCallback(() => {
    mapLoadingRef.current = true;
    mapLoadingStartedAtRef.current = Date.now();
    dispatch(selectionsSliceActions.setMapLoading(true));
  }, [dispatch]);

  const finishMapLoading = useCallback(() => {
    const elapsed = Date.now() - mapLoadingStartedAtRef.current;
    const delay = Math.max(300 - elapsed, 0);
    window.setTimeout(() => {
      dispatch(selectionsSliceActions.setMapLoading(false));
    }, delay);
  }, [dispatch]);

  // Safety net for the initial load: if the country figures never arrive
  // (failed/empty fetch), don't leave the loader hanging forever.
  useEffect(() => {
    if (allCountriesList?.length) return;
    const timer = window.setTimeout(() => {
      dispatch(selectionsSliceActions.setMapLoading(false));
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [dispatch, allCountriesList]);
  
  // useEffect(() => {    
  //  const endpointUrl = `https://gisportal.iom.int/arcgis/rest/services/REF/Global_DTM_ADM1_SIM/MapServer/0/query?where=ISO3%3D%27${selectedCountryPcode}%27&text=&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=ADM1_CODE%2CADM1_NAME%2CCOUNTRY&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&having=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&queryByDistance=&returnExtentOnly=false&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=pjson`;
  // async function fetchData() {
  //     try {
  //       const response = await fetch(`${endpointUrl}`, {
  //         method: "GET",
    
  //         headers: {
  //           "Content-Type": "application/x-www-form-urlencoded",
  //         },
  //       });

  //       if (!response.ok) {
  //         throw new Error(`An error occured: ${response.status}`);
  //       }

  //       const data = await response.json();
        

  //     } catch (error) {
  //       console.error("Failed to fetch country matrix data:", error);
  //     }
  //   }

  //   fetchData();
  // }, [selectedCountryPcode])
  
  // Operation is no longer a fetch parameter (we fetch all operations and filter
  // client-side), so these don't depend on selectedOperation — changing the
  // operation re-derives from the already-loaded data without a refetch.
  useEffect(() => {
    if (!selectedAdmin1Pcode) return;
    dispatch(getAdmin2(selectedAdmin1Pcode, selectedYear, selectedCause));
    // selectedRoundFilter is a dep so that pinning a round the DB lacks
    // re-runs the fetch and lets the live-API fallback fire.
  }, [dispatch, selectedAdmin1Pcode, selectedCause, selectedYear, selectedRoundFilter]);

  useEffect(() => {
    if (!selectedCountryPcode || selectedAdmin1 || selectedAdmin1Pcode) return;
    dispatch(getAdmin1(selectedCountryPcode, selectedYear, selectedCause));
    // See getAdmin2 above: refetch when the pinned round changes.
  }, [dispatch, selectedCountryPcode, selectedCause, selectedYear, selectedRoundFilter]);


  const backToRegions = () => {
    // Navigating the map shows the latest round, not a source-pinned one.
    clearIdpMovementMode();
    dispatch(selectionsSliceActions.setRoundFilter(null));
    if (selectedAdmin2) {
      dispatch(selectionsSliceActions.setAdmin2(""));
      dispatch(selectionsSliceActions.setAdmin1(""));
      dispatch(selectionsSliceActions.setAdmin1Pcode(""));
      return;
    }
    if (selectedAdmin1) {
      dispatch(selectionsSliceActions.setAdmin2(""));
      dispatch(selectionsSliceActions.setAdmin1(""));
      dispatch(selectionsSliceActions.setAdmin1Pcode(""));
      return;
    }
    if (selectedCountry) {
      // Back from a country returns to the global all-countries view.
      dispatch(selectionsSliceActions.setCountry(""));
      dispatch(selectionsSliceActions.setAdmin2(""));
      dispatch(selectionsSliceActions.setAdmin1(""));
      dispatch(selectionsSliceActions.setAdmin1Pcode(""));
      dispatch(selectionsSliceActions.setCountryPcode(""));
      dispatch(selectionsSliceActions.setRegion(""));
      return;
    }
  };

  const selectyear = (year) => {
    if (mapLoading || year === selectedYear) return;
    clearIdpMovementMode();
    startMapLoading();
    dispatch(selectionsSliceActions.setRoundFilter(null));
    dispatch(selectionsSliceActions.setYear(year));
  };
  const filteredRegions = useMemo(() => derivedRegions, [derivedRegions]);
  // All countries globally (no region filter) — the top level of the map now
  // opens on all countries instead of the region circles.
  const allCountries = useMemo(() => {
    const source = allCountriesList || [];
    if (!source.length) return [];
    const selectedYearNumber = Number(selectedYear);
    const hasSelectedYear = Number.isFinite(selectedYearNumber);
    const aggMap = new Map();

    source.forEach((row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
      if (!matchesSelectedCause(row.reason_for_displacement, selectedCause))
        return;

      const id =
        row.adm0_0_pcode ||
        row.adm0_0_name ||
        row.admin_0_name ||
        `${row.latitude ?? row.Latitude}-${row.longitude ?? row.Longitude}`;
      if (!id) return;

      const lat = Number(row.latitude ?? row.Latitude);
      const lng = Number(row.longitude ?? row.Longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const entry = aggMap.get(id) ?? {
        id,
        name: row.adm0_0_name || row.admin_0_name || "Unknown",
        pcode: row.admin_0_pcode || "",
        lat,
        lng,
        value: 0,
        maleidps: 0,
        femaleidps: 0,
        round: "",
        assessmentDate: "",
        percent: "0%",
      };
      const idpsValue = Number.isFinite(row.idps) ? row.idps : 0;
      const maleValue = Number.isFinite(row.male) ? row.male : 0;
      const femaleValue = Number.isFinite(row.female) ? row.female : 0;
      entry.value += idpsValue;
      entry.maleidps += maleValue;
      entry.femaleidps += femaleValue;
      const roundValue = row.round ?? row.Round ?? row.rounds;
      if (Number.isFinite(Number(roundValue))) {
        const currentRound = Number(entry.round) || 0;
        entry.round = Math.max(currentRound, Number(roundValue));
      } else if (!entry.round && roundValue) {
        entry.round = roundValue;
      }
      entry.assessmentDate = chooseLatestAssessmentDate(
        entry.assessmentDate,
        getRowAssessmentDate(row),
      );
      aggMap.set(id, entry);
    });

    const countries = Array.from(aggMap.values());
    const total = countries.reduce((sum, country) => sum + country.value, 0);
    return countries.map((country) => ({
      ...country,
      percent: formatPercent(country.value, total),
    }));
  }, [allCountriesList, selectedCause, selectedYear]);

  const admin1InSelectedCountry = useMemo(() => {
    if (!selectedCountry) return [];
    const source = admin1Data || [];
    if (!source.length) return [];
    const selectedYearNumber = Number(selectedYear);
    const hasSelectedYear = Number.isFinite(selectedYearNumber);
    const effectiveCause =
      selectedCause && selectedCause !== "All" ? selectedCause : "";
    const effectiveOperation =
      selectedOperation && selectedOperation !== "All" ? selectedOperation : "";
    const aggMap = new Map();

    // Isolate a single round so figures don't sum across rounds of the same
    // operation. Use the selected round when available, else the latest.
    const passesFilters = (row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return false;
      if (!matchesSelectedCause(row.reason_for_displacement, effectiveCause)) {
        return false;
      }
      const operation = row.operation || row.Operation || row.operation_name;
      if (effectiveOperation && operation !== effectiveOperation) return false;
      return true;
    };
    const targetRound = resolveTargetRound(
      source.filter(passesFilters),
      selectedRoundFilter,
    );

    const addRow = (row, forceYear) => {
      if (!matchesSelectedCause(row.reason_for_displacement, effectiveCause)) {
        return;
      }
      const operation = row.operation || row.Operation || row.operation_name;
      if (effectiveOperation && operation !== effectiveOperation) {
        return;
      }
      if (targetRound != null && getRowRound(row) !== targetRound) {
        return;
      }
      const name =
        row.admin_1_name ||
        row.admin1_name ||
        row.admin_1 ||
        row.admin1 ||
        row.admin_1_name_en;
      if (!name) return;
      const yearValue = Number(row.year);
      const yearKey = Number.isFinite(yearValue)
        ? yearValue
        : forceYear ?? selectedYearNumber;
      const key = `${normalizeRegionName(name)}-${yearKey}`;
      const entry = aggMap.get(key) ?? {
        id: row.admin_1_pcode || row.admin1_pcode || name,
        admin1_pcode: row.admin_1_pcode || row.admin1_pcode || "",
        name,
        year: yearKey,
        admin0_pcode: row.admin_0_pcode || row.admin0_pcode || "",
        lat: null,
        lng: null,
        value: 0,
        maleidps: 0,
        femaleidps: 0,
        round: "",
        assessmentDate: "",
        percent: "0%",
        rows: [],
      };
      const idpsValue = Number.isFinite(row.idps) ? row.idps : 0;
      const maleValue = Number.isFinite(row.male) ? row.male : 0;
      const femaleValue = Number.isFinite(row.female) ? row.female : 0;
      entry.value += idpsValue;
      entry.maleidps += maleValue;
      entry.femaleidps += femaleValue;
      const roundValue =
        row.round_number ?? row.round ?? row.Round ?? row.rounds;
      if (Number.isFinite(Number(roundValue))) {
        const currentRound = Number(entry.round) || 0;
        entry.round = Math.max(currentRound, Number(roundValue));
      } else if (!entry.round && roundValue) {
        entry.round = roundValue;
      }
      entry.assessmentDate = chooseLatestAssessmentDate(
        entry.assessmentDate,
        getRowAssessmentDate(row),
      );
      const coords = getLatLng(row);
      if (coords) {
        entry.lat = coords.lat;
        entry.lng = coords.lng;
      }
      if (!Array.isArray(entry.rows)) {
        entry.rows = [];
      }
      entry.rows.push(row);
      aggMap.set(key, entry);
    };

    source.forEach((row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
      addRow(row, selectedYearNumber);
    });

    const admin1s = Array.from(aggMap.values());
    const total = admin1s.reduce((sum, admin) => sum + admin.value, 0);
    return admin1s.map((admin) => ({
      ...admin,
      percent: formatPercent(admin.value, total),
    }));
  }, [admin1Data, selectedCountry, selectedCause, selectedOperation, selectedYear, selectedRoundFilter]);

  const admin1WithCoords = useMemo(
    () =>
      admin1InSelectedCountry.filter(
        (admin) => Number.isFinite(admin.lat) && Number.isFinite(admin.lng),
      ),
    [admin1InSelectedCountry],
  );
  const currentRoundHasOriginAdmin1Data = useMemo(
    () =>
      admin1InSelectedCountry.some((admin1) =>
        (admin1.rows || []).some(hasOriginAdmin1Data),
      ),
    [admin1InSelectedCountry],
  );
  // (Removed) admin 0 country-outline drawing — it only applied to the old
  // region-countries level, which no longer exists now that the map opens on all
  // countries. It also shared boundaryPolygonsRef with the admin 1/2 boundary
  // effects, so keeping it would wipe those outlines.

  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = mapsRef.current;
    if (mapStatus !== "ready") return;
    if (!map || !maps) return;
    if (!selectedCountryPcode || selectedAdmin1) return;
    const shadedCodes = new Set(
      admin1InSelectedCountry
        .map((admin) => admin.admin1_pcode)
        .filter(Boolean)
        .map((code) => String(code)),
    );
    const shadedNames = new Set(
      admin1InSelectedCountry
        .map((admin) => normalizeRegionName(admin.name))
        .filter(Boolean),
    );
    const admin1ByCode = new Map();
    const admin1ByName = new Map();
    admin1InSelectedCountry.forEach((admin1) => {
      if (admin1.admin1_pcode) {
        admin1ByCode.set(String(admin1.admin1_pcode), admin1);
      }
      admin1ByName.set(normalizeRegionName(admin1.name), admin1);
    });
    if (!shadedCodes.size && !shadedNames.size) return;
    boundaryPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    boundaryPolygonsRef.current = [];
    admin1PolygonRefs.current.clear();
    const controller = new AbortController();
    const url = buildAdm1BoundaryUrl(selectedCountryPcode);
    fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || controller.signal.aborted) return;
        const features = Array.isArray(data.features) ? data.features : [];
        boundaryPolygonsRef.current = createAdm1BoundaryPolygons(
          maps,
          map,
          features,
          shadedCodes,
          shadedNames,
          admin1ByCode,
          admin1ByName,
          admin1PolygonRefs,
          (admin1) => openAdmin1InfoOverlayRef.current?.(maps, map, admin1),
          (admin1Key, isHovered) =>
            admin1PolygonHoverRef.current?.(admin1Key, isHovered),
        );
        applyHighlightedAdmin1Ref.current?.();
        // Fit to the full country outline (all admin1 features), so every corner
        // stays in frame regardless of the operation filter.
        const bounds = computeFeatureBounds(maps, features);
        if (bounds) map.fitBounds(bounds, 40);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Failed to load admin1 boundaries", error);
      });
    return () => controller.abort();
  }, [admin1InSelectedCountry, mapStatus, selectedCountryPcode, selectedAdmin1]);

  // Back at the all-countries level there are no admin boundaries to show. The
  // admin1 boundary effect bails out early when selectedCountryPcode is empty
  // (before it clears), so clear any leftover outlines here.
  useEffect(() => {
    if (selectedCountryPcode) return;
    boundaryPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    boundaryPolygonsRef.current = [];
    admin1PolygonRefs.current.clear();
  }, [selectedCountryPcode]);

  const admin2InSelectedAdmin1 = useMemo(() => {
    if (!selectedCountry || !selectedAdmin1) return [];
    const source = admin2Data || [];
    if (!source.length) return [];
    const selectedYearNumber = Number(selectedYear);
    const hasSelectedYear = Number.isFinite(selectedYearNumber);
    const effectiveCause =
      selectedCause && selectedCause !== "All" ? selectedCause : "";
    const effectiveOperation =
      selectedOperation && selectedOperation !== "All" ? selectedOperation : "";
    const aggMap = new Map();

    const matchesAdmin1 = (row) => {
      const admin1Name =
        row.admin_1_name ||
        row.admin1_name ||
        row.admin_1 ||
        row.admin1 ||
        row.admin_1_name_en;
      return (
        !!admin1Name &&
        normalizeRegionName(admin1Name) === normalizeRegionName(selectedAdmin1)
      );
    };

    // Isolate a single round (selected when available, else latest) so admin 2
    // figures don't sum across rounds of the same operation.
    const passesFilters = (row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return false;
      if (!matchesSelectedCause(row.reason_for_displacement, effectiveCause)) {
        return false;
      }
      const operation = row.operation || row.Operation || row.operation_name;
      if (effectiveOperation && operation !== effectiveOperation) return false;
      return matchesAdmin1(row);
    };
    const targetRound = resolveTargetRound(
      source.filter(passesFilters),
      selectedRoundFilter,
    );

    const addRow = (row, forceYear) => {
      if (!matchesSelectedCause(row.reason_for_displacement, effectiveCause)) {
        return;
      }
      const operation = row.operation || row.Operation || row.operation_name;
      if (effectiveOperation && operation !== effectiveOperation) {
        return;
      }
      if (targetRound != null && getRowRound(row) !== targetRound) {
        return;
      }
      if (!matchesAdmin1(row)) return;
      const name =
        row.admin_2_name ||
        row.admin2_name ||
        row.admin_2 ||
        row.admin2 ||
        row.admin_2_name_en;
      if (!name) return;
      const yearValue = Number(row.year);
      const yearKey = Number.isFinite(yearValue)
        ? yearValue
        : forceYear ?? selectedYearNumber;
      const key = `${normalizeRegionName(name)}-${yearKey}`;
      const entry = aggMap.get(key) ?? {
        id: row.admin_2_pcode || row.admin2_pcode || name,
        name,
        year: yearKey,
        lat: null,
        lng: null,
        value: 0,
        maleidps: 0,
        femaleidps: 0,
        round: "",
        assessmentDate: "",
        percent: "0%",
        rows: [],
      };
      const idpsValue = Number.isFinite(row.idps) ? row.idps : 0;
      const maleValue = Number.isFinite(row.male) ? row.male : 0;
      const femaleValue = Number.isFinite(row.female) ? row.female : 0;
      entry.value += idpsValue;
      entry.maleidps += maleValue;
      entry.femaleidps += femaleValue;
      const roundValue =
        row.round_number ?? row.round ?? row.Round ?? row.rounds;
      if (Number.isFinite(Number(roundValue))) {
        const currentRound = Number(entry.round) || 0;
        entry.round = Math.max(currentRound, Number(roundValue));
      } else if (!entry.round && roundValue) {
        entry.round = roundValue;
      }
      entry.assessmentDate = chooseLatestAssessmentDate(
        entry.assessmentDate,
        getRowAssessmentDate(row),
      );
      const coords = getLatLng(row);
      if (coords) {
        entry.lat = coords.lat;
        entry.lng = coords.lng;
      }
      if (!Array.isArray(entry.rows)) {
        entry.rows = [];
      }
      entry.rows.push(row);
      aggMap.set(key, entry);
    };

    source.forEach((row) => {
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
      addRow(row, selectedYearNumber);
    });

    const admin2s = Array.from(aggMap.values());
    const total = admin2s.reduce((sum, admin) => sum + admin.value, 0);
    return admin2s.map((admin) => ({
      ...admin,
      percent: formatPercent(admin.value, total),
    }));
  }, [admin2Data, selectedAdmin1, selectedCountry, selectedCause, selectedOperation, selectedYear, selectedRoundFilter]);

  // Keep the right-sidebar summary figures in sync with the active
  // cause/year/operation filters for the selected country/admin, instead of
  // only updating them on circle click. Re-derives from the filtered data so
  // the figures (and zero/empty states) always match the current selection.
  useEffect(() => {
    if (!selectedCountry) return;

    // Admin levels: read from their resolved aggregate lists.
    if (selectedAdmin2 || selectedAdmin1) {
      const source = selectedAdmin2
        ? admin2InSelectedAdmin1
        : admin1InSelectedCountry;
      if (!source || !source.length) return;
      const target = normalizeRegionName(selectedAdmin2 || selectedAdmin1);
      const match = source.find(
        (item) => normalizeRegionName(item.name) === target,
      );
      dispatch(selectionsSliceActions.setidps(match ? getItemValue(match) : 0));
      dispatch(selectionsSliceActions.setmaleidps(match ? match.maleidps ?? 0 : 0));
      dispatch(selectionsSliceActions.setfemaleidps(match ? match.femaleidps ?? 0 : 0));
      dispatch(selectionsSliceActions.setRound(match ? match.round ?? 0 : 0));
      dispatch(selectionsSliceActions.setAssessmentDate(match ? getItemAssessmentDate(match) : ""));
      return;
    }

    // Country level with an operation selected: the country aggregate has no
    // operation breakdown, so sum the operation-filtered admin1 figures instead.
    if (selectedOperation && selectedOperation !== "All") {
      const list = admin1InSelectedCountry || [];
      let opValue = 0;
      let opMale = 0;
      let opFemale = 0;
      let opRound = 0;
      let opAssessmentDate = "";
      list.forEach((item) => {
        opValue += getItemValue(item) || 0;
        opMale += Number(item.maleidps) || 0;
        opFemale += Number(item.femaleidps) || 0;
        opRound = Math.max(opRound, Number(item.round) || 0);
        opAssessmentDate = chooseLatestAssessmentDate(
          opAssessmentDate,
          getItemAssessmentDate(item),
        );
      });
      dispatch(selectionsSliceActions.setidps(opValue));
      dispatch(selectionsSliceActions.setmaleidps(opMale));
      dispatch(selectionsSliceActions.setfemaleidps(opFemale));
      dispatch(selectionsSliceActions.setRound(opRound));
      dispatch(selectionsSliceActions.setAssessmentDate(opAssessmentDate));
      return;
    }

    // Country level: aggregate directly from the global country list, filtered
    // by the selected country + cause + year. Independent of region context, so
    // it works for both map drill-downs and the AI "Visualize this data" path.
    const source = allCountriesList || [];
    if (!source.length) return;
    const normalizedCountry = normalizeRegionName(selectedCountry);
    const selectedYearNumber = Number(selectedYear);
    const hasSelectedYear = Number.isFinite(selectedYearNumber);

    let value = 0;
    let male = 0;
    let female = 0;
    let round = 0;
    let assessmentDate = "";
    let matched = false;

    source.forEach((row) => {
      const rowName = normalizeRegionName(row.adm0_0_name || row.admin_0_name);
      const isCountry =
        rowName === normalizedCountry ||
        (selectedCountryPcode && row.admin_0_pcode === selectedCountryPcode);
      if (!isCountry) return;
      if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
      if (!matchesSelectedCause(row.reason_for_displacement, selectedCause)) return;
      matched = true;
      value += Number.isFinite(row.idps) ? row.idps : 0;
      male += Number.isFinite(row.male) ? row.male : 0;
      female += Number.isFinite(row.female) ? row.female : 0;
      const roundValue = Number(row.round ?? row.Round ?? row.rounds);
      if (Number.isFinite(roundValue)) round = Math.max(round, roundValue);
      assessmentDate = chooseLatestAssessmentDate(
        assessmentDate,
        getRowAssessmentDate(row),
      );
    });

    dispatch(selectionsSliceActions.setidps(matched ? value : 0));
    dispatch(selectionsSliceActions.setmaleidps(matched ? male : 0));
    dispatch(selectionsSliceActions.setfemaleidps(matched ? female : 0));
    dispatch(selectionsSliceActions.setRound(matched ? round : 0));
    dispatch(selectionsSliceActions.setAssessmentDate(matched ? assessmentDate : ""));
  }, [
    dispatch,
    selectedCountry,
    selectedCountryPcode,
    selectedAdmin1,
    selectedAdmin2,
    selectedCause,
    selectedYear,
    selectedOperation,
    allCountriesList,
    admin1InSelectedCountry,
    admin2InSelectedAdmin1,
  ]);

  // When a country is selected without a region context (e.g. from the AI
  // "Visualize this data" action), resolve its region from the country list so
  // the region pipeline + summary figures populate as with a normal drill-down.
  useEffect(() => {
    if (!selectedCountry) return;
    const source = allCountriesList || [];
    const normalizedCountry = normalizeRegionName(selectedCountry);
    const match = source.find(
      (row) =>
        normalizeRegionName(row.adm0_0_name || row.admin_0_name) ===
          normalizedCountry ||
        (selectedCountryPcode && row.admin_0_pcode === selectedCountryPcode),
    );
    const regionName = match?.region || match?.region_name || match?.regionName;
    // Always keep the region in sync with the selected country. A country
    // belongs to exactly one region, so this is a no-op during normal region
    // drill-down but corrects the stale region when switching countries via the
    // AI "Visualize this data" path (e.g. Lebanon → Nigeria).
    if (
      regionName &&
      normalizeRegionName(regionName) !== normalizeRegionName(selectedRegion)
    ) {
      dispatch(selectionsSliceActions.setRegion(regionName));
    }
  }, [
    dispatch,
    selectedCountry,
    selectedCountryPcode,
    selectedRegion,
    allCountriesList,
  ]);

  // Keep the "Overall IDPs Observed" trend chart in sync with the selected
  // country for any selection path (map click or AI "Visualize this data").
  useEffect(() => {
    if (!selectedCountryPcode) return;
    dispatch(getOverallIdps(selectedCountryPcode));
  }, [dispatch, selectedCountryPcode]);

  const admin2WithCoords = useMemo(
    () =>
      admin2InSelectedAdmin1.filter(
        (admin) => Number.isFinite(admin.lat) && Number.isFinite(admin.lng),
      ),
    [admin2InSelectedAdmin1],
  );
  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = mapsRef.current;
    if (mapStatus !== "ready") return;
    if (!map || !maps) return;
    if (!selectedCountryPcode || !selectedAdmin1) return;
    const shadedCodes = new Set(
      admin2InSelectedAdmin1
        .map((admin) => admin.id)
        .filter(Boolean)
        .map((code) => String(code)),
    );
    const shadedNames = new Set(
      admin2InSelectedAdmin1
        .map((admin) => normalizeRegionName(admin.name))
        .filter(Boolean),
    );
    if (!shadedCodes.size && !shadedNames.size) return;
    boundaryPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    boundaryPolygonsRef.current = [];
    const controller = new AbortController();
    const url = buildAdm2BoundaryUrl(selectedCountryPcode);
    fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        console.log("admin2data", data);
        
        if (!data || controller.signal.aborted) return;
        const features = Array.isArray(data.features) ? data.features : [];
        boundaryPolygonsRef.current = createAdm2BoundaryPolygons(
          maps,
          map,
          features,
          shadedCodes,
          shadedNames,
        );
        // Fit to the full selected admin1 (all of its admin2 features), matched
        // by parent admin1 so the extent is independent of the operation filter.
        const parentCode = selectedAdmin1Pcode ? String(selectedAdmin1Pcode) : null;
        const parentName = normalizeRegionName(selectedAdmin1);
        const bounds = computeFeatureBounds(maps, features, (feature) => {
          const code = feature?.attributes?.ADM1_CODE;
          const name = normalizeRegionName(feature?.attributes?.ADM1_NAME);
          return (
            (parentCode && code && String(code) === parentCode) ||
            (parentName && name && name === parentName)
          );
        });
        if (bounds) map.fitBounds(bounds, 40);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Failed to load admin2 boundaries", error);
      });
    return () => controller.abort();
  }, [admin2InSelectedAdmin1, mapStatus, selectedAdmin1, selectedAdmin1Pcode, selectedCountryPcode]);
  const legendItems = useMemo(
    () =>
      selectedAdmin1
        ? admin2InSelectedAdmin1
        : selectedCountry
          ? admin1InSelectedCountry
          : allCountries,
    [
      admin1InSelectedCountry,
      admin2InSelectedAdmin1,
      allCountries,
      selectedAdmin1,
      selectedCountry,
    ],
  );
  const movementLegendItems = useMemo(() => {
    if (!movementAdmin1) return [];

    const selectedKey = getAdmin1Key(movementAdmin1);
    const grouped = new Map();
    const addMovementValue = (key, value) => {
      const numericValue = toFiniteNumber(value);
      if (!key || numericValue <= 0) return;
      grouped.set(key, {
        value: (grouped.get(key)?.value || 0) + numericValue,
      });
    };

    if (movementDirection === "departing") {
      admin1WithCoords.forEach((destinationAdmin1) => {
        (destinationAdmin1.rows || []).forEach((row) => {
          const originPcode = getOriginAdmin1Pcode(row);
          const originName = getOriginAdmin1Name(row);
          const originMatches =
            (originPcode &&
              movementAdmin1.admin1_pcode &&
              String(originPcode) === String(movementAdmin1.admin1_pcode)) ||
            (originName &&
              normalizeRegionName(originName) === normalizeRegionName(movementAdmin1.name));
          if (!originMatches) return;

          const destinationKey = getAdmin1Key(destinationAdmin1);
          addMovementValue(
            destinationKey === selectedKey ? `${selectedKey}-internal` : destinationKey,
            row?.idps ?? row?.total_idps ?? row?.value,
          );
        });
      });
    } else {
      (movementAdmin1.rows || []).forEach((row) => {
        const originPcode = getOriginAdmin1Pcode(row);
        const originName = getOriginAdmin1Name(row);
        if (
          !hasUsableOriginAdmin1Value(originPcode) &&
          !hasUsableOriginAdmin1Value(originName)
        ) {
          return;
        }
        const isInternal =
          (originPcode &&
            movementAdmin1.admin1_pcode &&
            String(originPcode) === String(movementAdmin1.admin1_pcode)) ||
          (originName &&
            normalizeRegionName(originName) === normalizeRegionName(movementAdmin1.name));
        const originKey = String(originPcode || normalizeRegionName(originName || ""));
        if (!originKey) return;
        addMovementValue(
          isInternal ? `${selectedKey}-internal` : originKey,
          row?.idps ?? row?.total_idps ?? row?.value,
        );
      });
    }

    return Array.from(grouped.values());
  }, [admin1WithCoords, movementAdmin1, movementDirection]);
  const hasMovementLegendData = movementLegendItems.some((item) => {
    const value = getItemValue(item);
    return Number.isFinite(value) && value > 0;
  });
  const legendValues = useMemo(
    () =>
      movementAdmin1 && hasMovementLegendData
        ? getLegendValues(movementLegendItems, "total")
        : getLegendValues(legendItems, populationType),
    [
      hasMovementLegendData,
      legendItems,
      movementAdmin1,
      movementLegendItems,
      populationType,
    ],
  );
  // Smallest legend tick — the lower reference for log symbol sizing.
  const legendMinValue = useMemo(() => {
    const positives = (legendValues || []).filter(
      (value) => Number.isFinite(value) && value > 0,
    );
    return positives.length ? Math.min(...positives) : 0;
  }, [legendValues]);
  const hasData = useMemo(
    () =>
      legendItems.some((item) => {
        const value = getItemValue(item);
        return Number.isFinite(value) && value > 0;
      }),
    [legendItems],
  );
  const showLegend = movementAdmin1 ? hasMovementLegendData : hasData;
  const legendTitle = movementAdmin1
    ? movementDirection === "departing"
      ? "Departing IDPs"
      : "Arriving IDPs"
    : "Displacement Population";
  const legendColor = movementAdmin1 ? getMovementColors(movementDirection).base : ADMIN1_DEFAULT_COLOR;

  // Whether the current level has any male/female breakdown, so the population
  // toggle can disable those options when there's nothing to show.
  const hasMaleData = useMemo(
    () => legendItems.some((item) => Number(item.maleidps) > 0),
    [legendItems],
  );
  const hasFemaleData = useMemo(
    () => legendItems.some((item) => Number(item.femaleidps) > 0),
    [legendItems],
  );
  // If the active level has no male/female breakdown, fall back to Total so a
  // metric with no data can't stay selected (e.g. after the AI auto-selects a
  // sex from the question, but that data isn't disaggregated). Only act once
  // data is present, so we don't override a valid selection mid-load.
  useEffect(() => {
    if (!legendItems.length) return;
    if (
      (populationType === "male" && !hasMaleData) ||
      (populationType === "female" && !hasFemaleData)
    ) {
      dispatch(selectionsSliceActions.setPopulationType("total"));
    }
  }, [legendItems.length, populationType, hasMaleData, hasFemaleData, dispatch]);

  // When the map is ready but the current selection has no figures, we show the
  // centered "no data" card. The info panel is hidden while this is on screen so
  // the two don't overlap.
  const showEmptyState = mapStatus === "ready" && !mapLoading && !hasData;

  // Single source of truth for the empty-state copy, shared by the centered
  // map overlay and the info-panel fallback so they never drift apart.
  const noDataMessage = useMemo(() => {
    const when =
      selectedCause && selectedCause !== "All"
        ? `${selectedCause} in ${selectedYear}`
        : selectedYear;
    const operation =
      selectedOperation && selectedOperation !== "All"
        ? ` under the ${selectedOperation} operation`
        : "";
    const round = selectedRoundFilter != null ? ` (round ${selectedRoundFilter})` : "";
    const where =
      selectedAdmin2 ||
      selectedAdmin1 ||
      selectedCountry ||
      activeRegion?.name;
    return `No displacement data available for ${when}${operation}${round}${
      where ? ` in ${where}` : ""
    }.`;
  }, [
    activeRegion,
    selectedAdmin1,
    selectedAdmin2,
    selectedCause,
    selectedCountry,
    selectedOperation,
    selectedRoundFilter,
    selectedYear,
  ]);

  // Region is now label-only metadata (breadcrumb / right sidebar) and no longer
  // drives the map view, so selecting a region/country never changes the level.
  const clearOverlays = (overlays) => {
    overlays.forEach((overlay) => {
      if (overlay && typeof overlay.setMap === "function") {
        overlay.setMap(null);
      } else if (overlay && typeof overlay.close === "function") {
        overlay.close();
      }
    });
  };
  const closeAdmin1InfoOverlay = useCallback(() => {
    admin1InfoOverlayRef.current?.setMap?.(null);
    admin1InfoOverlayRef.current = null;
  }, []);

  const clearMovementLayer = useCallback(() => {
    movementPolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    movementPolylinesRef.current = [];
    movementContributionCirclesRef.current.forEach((circle) => circle.setMap(null));
    movementContributionCirclesRef.current = [];
    movementLinkDetailsRef.current.clear();
    setMovementHoverDetail(null);
  }, []);

  const resetAdmin1CircleColors = useCallback(() => {
    admin1CircleRefs.current.forEach((circle) => {
      circle.setOptions({
        fillColor: ADMIN1_DEFAULT_COLOR,
        fillOpacity: 0.78,
        strokeOpacity: 1,
        clickable: true,
      });
    });
  }, []);

  const hideAdmin1Labels = useCallback(() => {
    admin1LabelRefs.current.forEach((labelOverlay) => {
      setOverlayVisibility(
        labelOverlay,
        false,
        classes.region_label_overlay_visible,
      );
    });
  }, []);

  const setAdmin1PolygonStyle = useCallback((admin1Key, style) => {
    const polygons = admin1PolygonRefs.current.get(admin1Key) || [];
    polygons.forEach((polygon) => {
      polygon.setOptions(style);
    });
  }, []);

  const resetAdmin1PolygonStyles = useCallback(() => {
    admin1MovementPolygonStylesRef.current.clear();
    admin1PolygonRefs.current.forEach((polygons) => {
      polygons.forEach((polygon) => polygon.setOptions(ADMIN1_POLYGON_DEFAULT));
    });
  }, []);

  const applyHighlightedAdmin1 = useCallback(() => {
    if (!highlightedAdmin1 || selectedAdmin1 || !selectedCountry) return;

    const highlightedKeys = getHighlightedAdmin1Keys(highlightedAdmin1);
    const matchedAdmin1 = admin1WithCoords.find((admin1) => {
      const adminKeys = getHighlightedAdmin1Keys(admin1);
      return adminKeys.some((key) => highlightedKeys.includes(key));
    });
    const highlightedKey = matchedAdmin1
      ? getAdmin1Key(matchedAdmin1)
      : getHighlightedAdmin1Key(highlightedAdmin1);
    if (!highlightedKey) return;

    setAdmin1PolygonStyle(highlightedKey, ADMIN1_POLYGON_SELECTED);
    const circle = admin1CircleRefs.current.get(highlightedKey);
    if (circle) {
      circle.setOptions({
        fillColor: ADMIN1_DEFAULT_COLOR,
        fillOpacity: 0.95,
        strokeColor: "#ffffff",
        strokeWeight: 4,
        zIndex: 999,
      });
    }

    const labelOverlay = admin1LabelRefs.current.get(highlightedKey);
    if (labelOverlay) {
      setOverlayVisibility(
        labelOverlay,
        true,
        classes.region_label_overlay_visible,
      );
    }
  }, [
    admin1WithCoords,
    highlightedAdmin1,
    selectedAdmin1,
    selectedCountry,
    setAdmin1PolygonStyle,
  ]);
  applyHighlightedAdmin1Ref.current = applyHighlightedAdmin1;

  useEffect(() => {
    if (movementAdmin1Ref.current) return;

    resetAdmin1CircleColors();
    resetAdmin1PolygonStyles();
    hideAdmin1Labels();
    applyHighlightedAdmin1();
  }, [
    applyHighlightedAdmin1,
    highlightedAdmin1,
    hideAdmin1Labels,
    resetAdmin1CircleColors,
    resetAdmin1PolygonStyles,
  ]);

  useEffect(() => {
    if (!highlightedAdmin1) return;
    if (selectedCountry && !selectedAdmin1) return;
    dispatch(selectionsSliceActions.setHighlightedAdmin1(null));
  }, [dispatch, highlightedAdmin1, selectedAdmin1, selectedCountry]);

  const clearIdpMovementMode = useCallback(() => {
    clearMovementLayer();
    resetAdmin1CircleColors();
    resetAdmin1PolygonStyles();
    setMovementAdmin1(null);
    dispatch(selectionsSliceActions.setIdpMovement(false));
    dispatch(selectionsSliceActions.setIdpMovementAdmin1(null));
  }, [clearMovementLayer, dispatch, resetAdmin1CircleColors, resetAdmin1PolygonStyles]);

  const setAdmin1PolygonHover = useCallback((admin1Key, isHovered) => {
    const highlightedKeys = highlightedAdmin1
      ? getHighlightedAdmin1Keys(highlightedAdmin1)
      : [];
    const matchedHighlightedAdmin1 = highlightedAdmin1
      ? admin1WithCoords.find((admin1) =>
          getHighlightedAdmin1Keys(admin1).some((key) =>
            highlightedKeys.includes(key),
          ),
        )
      : null;
    const matchedHighlightedKey = matchedHighlightedAdmin1
      ? getAdmin1Key(matchedHighlightedAdmin1)
      : "";

    if (
      !isHovered &&
      highlightedAdmin1 &&
      (highlightedKeys.includes(String(admin1Key)) ||
        matchedHighlightedKey === String(admin1Key))
    ) {
      dispatch(selectionsSliceActions.setHighlightedAdmin1(null));
    }

    const movementStyle = admin1MovementPolygonStylesRef.current.get(admin1Key);
    const movementDetail = movementLinkDetailsRef.current.get(admin1Key);
    const movementColors = movementAdmin1Ref.current
      ? getMovementColors(movementDirectionRef.current)
      : null;
    const hoverStyle = movementStyle
      ? {
          ...movementStyle,
          fillColor: movementColors?.base || movementStyle.fillColor,
          strokeColor: movementColors?.hover || movementStyle.strokeColor,
          fillOpacity: Math.min((movementStyle.fillOpacity ?? 0.18) + 0.08, 0.35),
          strokeOpacity: 0.95,
        }
      : ADMIN1_POLYGON_SELECTED;
    setAdmin1PolygonStyle(
      admin1Key,
      isHovered ? hoverStyle : movementStyle || ADMIN1_POLYGON_DEFAULT,
    );
    if (movementDetail) {
      setMovementHoverDetail(isHovered ? movementDetail : null);
    }
  }, [admin1WithCoords, dispatch, highlightedAdmin1, setAdmin1PolygonStyle]);
  admin1PolygonHoverRef.current = setAdmin1PolygonHover;

  const getMovementLinks = useCallback(
    (selectedMovementAdmin1, direction = "arrivals") => {
      if (!selectedMovementAdmin1) return [];

      const admin1ByCode = new Map();
      const admin1ByName = new Map();
      admin1WithCoords.forEach((admin1) => {
        if (admin1.admin1_pcode) {
          admin1ByCode.set(String(admin1.admin1_pcode), admin1);
        }
        admin1ByName.set(normalizeRegionName(admin1.name), admin1);
      });

      const selectedKey = getAdmin1Key(selectedMovementAdmin1);
      const links = new Map();

      const addLink = ({ row, fromAdmin1, toAdmin1, otherAdmin1, fallbackName }) => {
        const value = toFiniteNumber(row?.idps ?? row?.total_idps ?? row?.value);
        if (value <= 0 || !fromAdmin1 || !toAdmin1) return;

        const otherKey = otherAdmin1
          ? getAdmin1Key(otherAdmin1)
          : String(fallbackName || "");
        if (!otherKey || otherKey === selectedKey) return;

        const existing = links.get(otherKey);
        links.set(otherKey, {
          key: otherKey,
          name: otherAdmin1?.name || fallbackName || "Admin 1",
          from: { lat: fromAdmin1.lat, lng: fromAdmin1.lng },
          to: { lat: toAdmin1.lat, lng: toAdmin1.lng },
          value: (existing?.value || 0) + value,
        });
      };

      if (direction === "departing") {
        admin1WithCoords.forEach((destinationAdmin1) => {
          (destinationAdmin1.rows || []).forEach((row) => {
            const originPcode = getOriginAdmin1Pcode(row);
            const originName = getOriginAdmin1Name(row);
            const originMatches =
              (originPcode &&
                selectedMovementAdmin1.admin1_pcode &&
                String(originPcode) === String(selectedMovementAdmin1.admin1_pcode)) ||
              (originName &&
                normalizeRegionName(originName) ===
                  normalizeRegionName(selectedMovementAdmin1.name));
            if (!originMatches) return;

            addLink({
              row,
              fromAdmin1: selectedMovementAdmin1,
              toAdmin1: destinationAdmin1,
              otherAdmin1: destinationAdmin1,
              fallbackName: getDestinationAdmin1Name(row),
            });
          });
        });
        return Array.from(links.values());
      }

      (selectedMovementAdmin1.rows || []).forEach((row) => {
        const originPcode = getOriginAdmin1Pcode(row);
        const originName = getOriginAdmin1Name(row);
        const originKey = String(originPcode || normalizeRegionName(originName || ""));
        if (!originKey || originKey === selectedKey) return;

        const matchedAdmin1 =
          (originPcode && admin1ByCode.get(String(originPcode))) ||
          (originName && admin1ByName.get(normalizeRegionName(originName)));
        const coords = getOriginLatLng(row) || matchedAdmin1;
        if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) {
          return;
        }

        const key = matchedAdmin1 ? getAdmin1Key(matchedAdmin1) : originKey;
        addLink({
          row,
          fromAdmin1: { ...matchedAdmin1, lat: coords.lat, lng: coords.lng },
          toAdmin1: selectedMovementAdmin1,
          otherAdmin1: matchedAdmin1 || { id: key, name: originName },
          fallbackName: originName,
        });
      });

      return Array.from(links.values());
    },
    [admin1WithCoords],
  );

  const getInternalMovement = useCallback((selectedMovementAdmin1) => {
    if (!selectedMovementAdmin1) return null;
    const selectedPcode = selectedMovementAdmin1.admin1_pcode
      ? String(selectedMovementAdmin1.admin1_pcode)
      : "";
    const selectedName = normalizeRegionName(selectedMovementAdmin1.name);
    const value = (selectedMovementAdmin1.rows || []).reduce((total, row) => {
      const originPcode = getOriginAdmin1Pcode(row);
      const originName = getOriginAdmin1Name(row);
      const originMatches =
        (selectedPcode && originPcode && String(originPcode) === selectedPcode) ||
        (selectedName && normalizeRegionName(originName) === selectedName);
      if (!originMatches) return total;
      return total + toFiniteNumber(row?.idps ?? row?.total_idps ?? row?.value);
    }, 0);

    if (value <= 0) return null;
    return {
      lat: selectedMovementAdmin1.lat,
      lng: selectedMovementAdmin1.lng,
      value,
    };
  }, []);

  const renderIdpMovement = useCallback(
    (selectedMovementAdmin1, direction) => {
      const maps = mapsRef.current;
      const map = mapInstanceRef.current;
      if (!maps || !map || !selectedMovementAdmin1) return;

      clearMovementLayer();
      resetAdmin1CircleColors();
      resetAdmin1PolygonStyles();

      const links = getMovementLinks(selectedMovementAdmin1, direction);
      const internalMovement = getInternalMovement(selectedMovementAdmin1);
      const linkedKeys = new Set(links.map((link) => link.key));
      const selectedKey = getAdmin1Key(selectedMovementAdmin1);
      const relatedPolygonStyle = getMovementRelatedPolygonStyle(direction);
      movementLinkDetailsRef.current = new Map(
        links.map((link) => [
          link.key,
          getMovementDetail({
            link,
            direction,
            selectedName: selectedMovementAdmin1.name,
          }),
        ]),
      );

      if (!links.length && !internalMovement) {
        showMapToast(
          `No ${direction === "departing" ? "departing" : "arrival"} flow data found for ${selectedMovementAdmin1.name}.`,
        );
        console.warn("[IDP movement] No origin flow data found", {
          direction,
          selectedAdmin1: selectedMovementAdmin1,
          sampleRow: selectedMovementAdmin1.rows?.[0],
        });
      }

      admin1CircleRefs.current.forEach((circle, key) => {
        circle.setOptions({
          fillColor: ADMIN1_MOVEMENT_INACTIVE_COLOR,
          fillOpacity: 0,
          strokeOpacity: 0,
          clickable: false,
        });
      });

      admin1MovementPolygonStylesRef.current.set(
        selectedKey,
        ADMIN1_POLYGON_SELECTED,
      );
      setAdmin1PolygonStyle(selectedKey, ADMIN1_POLYGON_SELECTED);
      linkedKeys.forEach((key) => {
        admin1MovementPolygonStylesRef.current.set(key, relatedPolygonStyle);
        setAdmin1PolygonStyle(key, relatedPolygonStyle);
      });

      const movementOverlays = links.length
        ? createMovementMapOverlays(maps, map, {
            links,
            internalMovement,
            direction,
            selectedName: selectedMovementAdmin1.name,
            onHoverDetail: setMovementHoverDetail,
          })
        : internalMovement
          ? createMovementMapOverlays(maps, map, {
              links: [],
              internalMovement,
              direction,
              selectedName: selectedMovementAdmin1.name,
              onHoverDetail: setMovementHoverDetail,
            })
        : [];
      movementPolylinesRef.current = movementOverlays;
      console.log("[IDP movement] render", {
        direction,
        selectedAdmin1: selectedMovementAdmin1.name,
        links: links.length,
        internalMovement: internalMovement?.value || 0,
        linkedKeys: Array.from(linkedKeys),
        overlays: movementOverlays.length,
        sampleLink: links[0],
      });

      dispatch(selectionsSliceActions.setIdpMovement(true));
      dispatch(
        selectionsSliceActions.setIdpMovementAdmin1({
          name: selectedMovementAdmin1.name,
          admin1_pcode: selectedMovementAdmin1.admin1_pcode || "",
          value: selectedMovementAdmin1.value || 0,
          round: selectedMovementAdmin1.round || "",
          year: selectedMovementAdmin1.year || "",
        }),
      );
      dispatch(selectionsSliceActions.setIdpMovementDirection(direction));
    },
    [
      clearMovementLayer,
      dispatch,
      getInternalMovement,
      getMovementLinks,
      resetAdmin1CircleColors,
      resetAdmin1PolygonStyles,
      setAdmin1PolygonStyle,
      showMapToast,
    ],
  );

  useEffect(() => {
    if (!movementAdmin1) return;
    if (!selectedCountry || selectedAdmin1) return;
    renderIdpMovement(movementAdmin1, movementDirection);
  }, [movementAdmin1, movementDirection, renderIdpMovement, selectedAdmin1, selectedCountry]);

  useEffect(() => {
    if (!movementAdmin1) return;
    // Keep movement mode only while we stay at the country level of the SAME
    // country it was started in. Clear it when drilling into an admin1, going
    // back to all-countries, or switching to another country (e.g. via Ask Seb).
    // Otherwise movementAdmin1 (an admin1 of the old country) sticks around and
    // the admin1 view effect skips showAdmin1() — leaving the loader spinning.
    const movementCountryPcode = movementAdmin1.admin0_pcode || "";
    const stillOnSameCountryLevel =
      selectedCountry &&
      !selectedAdmin1 &&
      (!movementCountryPcode || movementCountryPcode === selectedCountryPcode);
    if (stillOnSameCountryLevel) return;
    clearIdpMovementMode();
  }, [
    clearIdpMovementMode,
    movementAdmin1,
    selectedAdmin1,
    selectedCountry,
    selectedCountryPcode,
  ]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = mapsRef.current;
    if (!map || !maps || !movementAdmin1) return undefined;
    const listener = map.addListener("click", () => {
      clearIdpMovementMode();
    });
    return () => {
      maps.event.removeListener(listener);
    };
  }, [clearIdpMovementMode, movementAdmin1]);

  const loadAdmin2Data = useCallback(
    (admin1) => {
      if (!admin1) return;

      clearIdpMovementMode();
      startMapLoading();
      dispatch(selectionsSliceActions.setLoadAdmin2Data(true));
      dispatch(selectionsSliceActions.setRoundFilter(null));
      if (admin1.admin1_pcode) {
        dispatch(
          getAdmin2(admin1.admin1_pcode, year, selectedCause, selectedOperation),
        );
      }
      dispatch(selectionsSliceActions.setAdmin1(admin1.name));
      dispatch(selectionsSliceActions.setAdmin1Pcode(admin1.admin1_pcode || ""));
      dispatch(selectionsSliceActions.setAdmin2(""));
      dispatch(selectionsSliceActions.setidps(admin1.value));
      dispatch(selectionsSliceActions.setmaleidps(admin1.maleidps));
      dispatch(selectionsSliceActions.setfemaleidps(admin1.femaleidps));
      dispatch(selectionsSliceActions.setRound(admin1.round));
      dispatch(selectionsSliceActions.setAssessmentDate(getItemAssessmentDate(admin1)));
      closeAdmin1InfoOverlay();
    },
    [
      clearIdpMovementMode,
      closeAdmin1InfoOverlay,
      dispatch,
      selectedCause,
      selectedOperation,
      startMapLoading,
      year,
    ],
  );

  const openAdmin1InfoOverlay = useCallback(
    (maps, map, admin1) => {
      if (!maps || !map || !admin1) return;

      closeAdmin1InfoOverlay();
      dispatch(selectionsSliceActions.setLoadAdmin2Data(false));

      const html = `
        <div class="${classes.admin_bubble_info}">
          <div class="${classes.admin_bubble_header}">
            <div class="${classes.admin_bubble_title}">${escapeHtml(admin1.name)}</div>
            <div class="${classes.admin_bubble_subtitle}">${escapeHtml(selectedCountry)}</div>
          </div>
          <div class="${classes.admin_bubble_metrics}">
            <div>
              <span class="${classes.admin_bubble_metric_value}">${formatFullValue(admin1.value)}</span>
              <span class="${classes.admin_bubble_metric_label}">Total</span>
            </div>
            <div>
              <span class="${classes.admin_bubble_metric_value}">${formatOptionalAdminBubbleValue(admin1.maleidps, classes.admin_bubble_metric_dash)}</span>
              <span class="${classes.admin_bubble_metric_label}">Male</span>
            </div>
            <div>
              <span class="${classes.admin_bubble_metric_value}">${formatOptionalAdminBubbleValue(admin1.femaleidps, classes.admin_bubble_metric_dash)}</span>
              <span class="${classes.admin_bubble_metric_label}">Female</span>
            </div>
          </div>
          <div class="${classes.admin_bubble_actions}">
            <button type="button" class="${classes.admin_bubble_action}" data-action="movement">
              IDP movement
            </button>
            <button type="button" class="${classes.admin_bubble_action}" data-action="breakdown">
              Admin 2 Breakdown
            </button>
          </div>
        </div>
      `;

      admin1InfoOverlayRef.current = createLabelOverlay(maps, map, {
        position: { lat: admin1.lat, lng: admin1.lng },
        offset: ({ map: overlayMap, point }) => {
          const mapDiv = overlayMap.getDiv?.();
          const mapHeight = mapDiv?.clientHeight || 0;
          const popupHeight = 176;
          const gap = 18;
          const edgePadding = 20;
          const roomAbove = point.y;
          const roomBelow = mapHeight - point.y;
          const openBelow =
            roomAbove < popupHeight + gap + edgePadding &&
            roomBelow > roomAbove;

          return {
            x: 0,
            y: openBelow ? gap : 0,
            placement: openBelow ? "below" : "above",
          };
        },
        className: classes.admin_bubble_info_overlay,
        html,
        value: admin1.value,
        onClick: (event) => {
          event.preventDefault();
          event.stopPropagation();
          const actionButton = event.target?.closest?.("[data-action]");
          if (!actionButton) return;

          const action = actionButton.getAttribute("data-action");
          if (action === "movement") {
            if (!currentRoundHasOriginAdmin1Data) {
              loadAdmin2Data(admin1);
              return;
            }
            dispatch(selectionsSliceActions.setLoadAdmin2Data(false));
            setMovementDirection("arrivals");
            setMovementAdmin1(admin1);
            closeAdmin1InfoOverlay();
            renderIdpMovement(admin1, "arrivals");
            return;
          }

          if (action === "breakdown") {
            loadAdmin2Data(admin1);
          }
        },
      });
    },
    [
      closeAdmin1InfoOverlay,
      currentRoundHasOriginAdmin1Data,
      dispatch,
      loadAdmin2Data,
      renderIdpMovement,
      selectedCountry,
    ],
  );

  const handleAdmin1MapSelection = useCallback(
    (maps, map, admin1) => {
      const activeMovementAdmin1 = movementAdmin1Ref.current;
      if (activeMovementAdmin1) {
        if (getAdmin1Key(activeMovementAdmin1) === getAdmin1Key(admin1)) {
          clearIdpMovementMode();
          return;
        }
        setMovementAdmin1(admin1);
        renderIdpMovement(admin1, movementDirectionRef.current);
        return;
      }
      if (!currentRoundHasOriginAdmin1Data) {
        closeAdmin1InfoOverlay();
        loadAdmin2Data(admin1);
        return;
      }
      openAdmin1InfoOverlay(maps, map, admin1);
    },
    [
      closeAdmin1InfoOverlay,
      clearIdpMovementMode,
      currentRoundHasOriginAdmin1Data,
      loadAdmin2Data,
      openAdmin1InfoOverlay,
      renderIdpMovement,
    ],
  );

  openAdmin1InfoOverlayRef.current = handleAdmin1MapSelection;

  // Keep symbol circles a constant on-screen pixel size across zoom levels.
  // google.maps.Circle radii are in meters, so without this they'd scale with
  // the map; we recompute meters = radiusPx × metersPerPixel(currentZoom).
  const resizeCircles = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const zoom = map.getZoom() ?? DEFAULT_ZOOM;
    const { regions, countries, admin1, admin2 } = overlaysRef.current;
    [
      ...regions,
      ...countries,
      ...admin1,
      ...admin2,
      ...movementContributionCirclesRef.current,
    ].forEach((overlay) => {
      if (overlay && overlay.__radiusPx && typeof overlay.setRadius === "function") {
        overlay.setRadius(overlay.__radiusPx * getMetersPerPixel(overlay.__lat, zoom));
      }
    });
  }, []);

  const createRegionOverlays = useCallback(
    (maps, map, list) => {
      const displayedTotal = (list || []).reduce(
        (sum, item) => sum + (getDisplayValue(item) || 0),
        0,
      );
      return list.flatMap((region) => {
        const value = getDisplayValue(region);
        const maxValue = Math.max(
          0,
          ...(legendValues || []).filter(Number.isFinite),
        );
        const radiusPx = getSymbolRadiusPx(value, maxValue, legendMinValue);
        const zoom = map?.getZoom?.() ?? DEFAULT_ZOOM;
        const regionRadius = radiusPx * getMetersPerPixel(region.lat, zoom);
            const circle = new maps.Circle({
                map,
                center: { lat: region.lat, lng: region.lng },
                radius: regionRadius,
                strokeColor: "#ffffff",
                strokeOpacity: 1,
                strokeWeight: 3,
                fillColor: "#0f8b36",
                fillOpacity: 0.85,
                clickable: true,
            });
        circle.__radiusPx = radiusPx;
        circle.__lat = region.lat;

        const labelOverlay = createLabelOverlay(maps, map, {
          position: { lat: region.lat, lng: region.lng },
          offset: { x: radiusPx + 6, y: 0 },
          className: classes.region_label_overlay,
          value,
          html: `
          <p><span class="${classes.region_value}">${formatValue(value)}</span>
          <span class="${classes.region_percent}">${formatPercent(value, displayedTotal)}</span></p>
          <span class="${classes.region_name}">${escapeHtml(region.name)}</span>
        `,
        });

        circle.addListener("click", () => {
          if (mapLoadingRef.current) return;
          clearIdpMovementMode();
          startMapLoading();
          dispatch(selectionsSliceActions.setRoundFilter(null));
          setActiveRegionId(region.id);
          dispatch(selectionsSliceActions.setRegion(region.name));
          dispatch(selectionsSliceActions.setCountry(""));
          dispatch(selectionsSliceActions.setAdmin2());
          dispatch(selectionsSliceActions.setAdmin1());
        });
        attachLabelReveal(circle, labelOverlay);

        return [circle, labelOverlay];
      });
    },
    [clearIdpMovementMode, legendValues, legendMinValue, dispatch, startMapLoading, getDisplayValue],
  );
  const createCountryOverlays = useCallback(
    (maps, map, list) => {
      const displayedTotal = (list || []).reduce(
        (sum, item) => sum + (getDisplayValue(item) || 0),
        0,
      );
      return list.flatMap((country) => {
        const value = getDisplayValue(country);
        const zoom = map?.getZoom?.() ?? DEFAULT_ZOOM;
        const metersPerPixel = getMetersPerPixel(country.lat, zoom);
        const maxValue = Math.max(
          0,
          ...(legendValues || []).filter(Number.isFinite),
        );
        const radiusPx = getSymbolRadiusPx(value, maxValue, legendMinValue);
        const circle = new maps.Circle({
          map,
          center: { lat: country.lat, lng: country.lng },
          radius: radiusPx * metersPerPixel,
          strokeColor: "#ffffff",
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: "#0f8b36",
          fillOpacity: 0.85,
          clickable: true,
        });
        circle.__radiusPx = radiusPx;
        circle.__lat = country.lat;

        const handleCountryClick = () => {
          if (mapLoadingRef.current) return;
          clearIdpMovementMode();
          startMapLoading();
          dispatch(selectionsSliceActions.setRoundFilter(null));
          // Operations are country-specific, so a stale operation from the
          // previously viewed country would filter out all data here. Reset it.
          dispatch(selectionsSliceActions.setOperation("All"));
          dispatch(getOverallIdps(country.pcode));
          dispatch(getAdmin1(country.pcode, year, selectedCause, "All"));
          dispatch(selectionsSliceActions.setCountry(country.name));
          dispatch(selectionsSliceActions.setCountryPcode(country.pcode || ""));
          dispatch(selectionsSliceActions.setAdmin1(""));
          dispatch(selectionsSliceActions.setAdmin2(""));
          dispatch(selectionsSliceActions.setidps(country.value));
          dispatch(selectionsSliceActions.setmaleidps(country.maleidps));
          dispatch(selectionsSliceActions.setfemaleidps(country.femaleidps));
          dispatch(selectionsSliceActions.setRound(country.round));
          dispatch(selectionsSliceActions.setAssessmentDate(getItemAssessmentDate(country)));
        };

        const labelOverlay = createLabelOverlay(maps, map, {
          position: { lat: country.lat, lng: country.lng },
          offset: { x: radiusPx + 6, y: 0 },
          className: `${classes.region_label_overlay} ${classes.region_label_overlay_hidden}`,
          value,
          onClick: handleCountryClick,
          html: `
          <p><span class="${classes.region_value}">${formatValue(value)}</span>
          <span class="${classes.region_percent}">${formatPercent(value, displayedTotal)}</span></p>
        `,
        });

        // Country figures stay hidden until hover (same as admin1/admin2).
        circle.addListener("mouseover", () => {
          setOverlayVisibility(
            labelOverlay,
            true,
            classes.region_label_overlay_visible,
          );
        });
        circle.addListener("mouseout", () => {
          setOverlayVisibility(
            labelOverlay,
            false,
            classes.region_label_overlay_visible,
          );
        });
        circle.addListener("click", handleCountryClick);
        return [circle, labelOverlay];
      });
    },
    [clearIdpMovementMode, dispatch, selectedCause, year, legendValues, legendMinValue, startMapLoading, getDisplayValue],
  );
  const createAdmin1Overlays = useCallback(
    (maps, map, list) => {
      const displayedTotal = (list || []).reduce(
        (sum, item) => sum + (getDisplayValue(item) || 0),
        0,
      );
      return list.flatMap((admin1) => {
        if (!Number.isFinite(admin1.lat) || !Number.isFinite(admin1.lng)) {
          return [];
        }
        const value = getDisplayValue(admin1);
        const zoom = map?.getZoom?.() ?? DEFAULT_ZOOM;
        const metersPerPixel = getMetersPerPixel(admin1.lat, zoom);
        const maxValue = Math.max(
          0,
          ...(legendValues || []).filter(Number.isFinite),
        );
        const radiusPx = getSymbolRadiusPx(value, maxValue, legendMinValue);
        const circle = new maps.Circle({
          map,
          center: { lat: admin1.lat, lng: admin1.lng },
          radius: radiusPx * metersPerPixel,
          strokeColor: "#ffffff",
          strokeOpacity: 1,
          strokeWeight: 2.5,
          fillColor: "#0f8b36",
          fillOpacity: 0.78,
          clickable: true,
        });
        circle.__radiusPx = radiusPx;
        circle.__lat = admin1.lat;
        circle.__admin1Key = getAdmin1Key(admin1);
        admin1CircleRefs.current.set(circle.__admin1Key, circle);

        const labelOverlay = createLabelOverlay(maps, map, {
          position: { lat: admin1.lat, lng: admin1.lng },
          offset: { x: radiusPx + 6, y: 0 },
          className: `${classes.region_label_overlay} ${classes.region_label_overlay_hidden}`,
          html: `
          <p><span class="${classes.region_value}">${formatValue(value)}</span>
          <span class="${classes.region_percent}">${formatPercent(value, displayedTotal)}</span></p>
          <span class="${classes.region_name}">${escapeHtml(admin1.name)}</span>
        `,
        });
        admin1LabelRefs.current.set(circle.__admin1Key, labelOverlay);
        if (
          highlightedAdmin1 &&
          circle.__admin1Key === getHighlightedAdmin1Key(highlightedAdmin1)
        ) {
          circle.setOptions({
            fillColor: ADMIN1_DEFAULT_COLOR,
            fillOpacity: 0.95,
            strokeColor: "#ffffff",
            strokeWeight: 4,
            zIndex: 999,
          });
          setOverlayVisibility(
            labelOverlay,
            true,
            classes.region_label_overlay_visible,
          );
        }

        circle.addListener("click", (event) => {
          if (mapLoadingRef.current) return;
          event?.stop?.();
          handleAdmin1MapSelection(maps, map, admin1);
        });
        circle.addListener("mouseover", () => {
          setAdmin1PolygonHover(circle.__admin1Key, true);
          setOverlayVisibility(
            labelOverlay,
            true,
            classes.region_label_overlay_visible,
          );
        });
        circle.addListener("mouseout", () => {
          const highlightedKeys = highlightedAdmin1
            ? getHighlightedAdmin1Keys(highlightedAdmin1)
            : [];
          const matchedHighlightedAdmin1 = highlightedAdmin1
            ? admin1WithCoords.find((item) =>
                getHighlightedAdmin1Keys(item).some((key) =>
                  highlightedKeys.includes(key),
                ),
              )
            : null;
          const matchedHighlightedKey = matchedHighlightedAdmin1
            ? getAdmin1Key(matchedHighlightedAdmin1)
            : "";
          if (
            highlightedAdmin1 &&
            (highlightedKeys.includes(String(circle.__admin1Key)) ||
              matchedHighlightedKey === String(circle.__admin1Key))
          ) {
            dispatch(selectionsSliceActions.setHighlightedAdmin1(null));
          }
          setAdmin1PolygonHover(circle.__admin1Key, false);
          setOverlayVisibility(
            labelOverlay,
            false,
            classes.region_label_overlay_visible,
          );
        });

        return [circle, labelOverlay];
      });
    },
    [admin1WithCoords, dispatch, highlightedAdmin1, legendValues, legendMinValue, handleAdmin1MapSelection, getDisplayValue, setAdmin1PolygonHover],
  );
  const createAdmin2Overlays = useCallback(
    (maps, map, list) => {
      const displayedTotal = (list || []).reduce(
        (sum, item) => sum + (getDisplayValue(item) || 0),
        0,
      );
      return list.flatMap((admin2) => {
        if (!Number.isFinite(admin2.lat) || !Number.isFinite(admin2.lng)) {
          return [];
        }
        const value = getDisplayValue(admin2);
        const zoom = map?.getZoom?.() ?? DEFAULT_ZOOM;
        const metersPerPixel = getMetersPerPixel(admin2.lat, zoom);
        const maxValue = Math.max(
          0,
          ...(legendValues || []).filter(Number.isFinite),
        );
        const radiusPx = getSymbolRadiusPx(value, maxValue, legendMinValue);
        const circle = new maps.Circle({
          map,
          center: { lat: admin2.lat, lng: admin2.lng },
          radius: radiusPx * metersPerPixel,
          strokeColor: "#ffffff",
          strokeOpacity: 1,
          strokeWeight: 2.5,
          fillColor: "#0f8b36",
          fillOpacity: 0.72,
          clickable: true,
        });
        circle.__radiusPx = radiusPx;
        circle.__lat = admin2.lat;
        circle.__isAdmin2Circle = true;

        const labelOverlay = createLabelOverlay(maps, map, {
          position: { lat: admin2.lat, lng: admin2.lng },
          offset: { x: radiusPx + 6, y: 0 },
          className: `${classes.region_label_overlay} ${classes.region_label_overlay_hidden}`,
          html: `
          <p><span class="${classes.region_value}">${formatValue(value)}</span>
          <span class="${classes.region_percent}">${formatPercent(value, displayedTotal)}</span></p>
          <span class="${classes.region_name}">${escapeHtml(admin2.name)}</span>
        `,
        });

        circle.addListener("click", () => {
          if (mapLoadingRef.current) return;
          // Admin 2 is the last drill level — selecting one only updates the
          // sidebar figures (no data reload), so don't flash the map loader.
          // Highlight the selected admin 2 by its boundary polygon: reset all
          // admin 2 polygons, then accent the one matching the clicked bubble.
          const targetCode = admin2.id ? String(admin2.id) : "";
          const targetName = normalizeRegionName(admin2.name);
          let polygonMatched = false;
          boundaryPolygonsRef.current.forEach((polygon) => {
            if (!polygon || !polygon.__isAdmin2Boundary) return;
            const isMatch =
              (targetCode && polygon.__admin2Code === targetCode) ||
              (targetName && polygon.__admin2Name === targetName);
            if (isMatch) polygonMatched = true;
            polygon.setOptions(
              isMatch ? ADMIN2_POLYGON_SELECTED : ADMIN2_POLYGON_DEFAULT,
            );
          });
          // Reset every bubble; only fall back to accenting the bubble itself
          // when no boundary polygon was found for this admin 2.
          overlaysRef.current.admin2.forEach((overlay) => {
            if (overlay && overlay.__isAdmin2Circle) {
              overlay.setOptions({
                strokeColor: "#ffffff",
                strokeWeight: 2.5,
                fillColor: "#0f8b36",
                fillOpacity: 0.72,
                zIndex: 1,
              });
            }
          });
          if (!polygonMatched) {
            circle.setOptions({
              strokeColor: "#ffffff",
              strokeWeight: 4,
              fillColor: "#0a5c27",
              fillOpacity: 1,
              zIndex: 999,
            });
          }
          dispatch(selectionsSliceActions.setAdmin2(admin2.name));
          dispatch(selectionsSliceActions.setidps(admin2.value));
          dispatch(selectionsSliceActions.setmaleidps(admin2.maleidps));
          dispatch(selectionsSliceActions.setfemaleidps(admin2.femaleidps));
          dispatch(selectionsSliceActions.setRound(admin2.round));
          dispatch(selectionsSliceActions.setAssessmentDate(getItemAssessmentDate(admin2)));
        });
        circle.addListener("mouseover", () =>
          setOverlayVisibility(
            labelOverlay,
            true,
            classes.region_label_overlay_visible,
          ),
        );
        circle.addListener("mouseout", () =>
          setOverlayVisibility(
            labelOverlay,
            false,
            classes.region_label_overlay_visible,
          ),
        );

        return [circle, labelOverlay];
      });
    },
    [dispatch, legendValues, legendMinValue, getDisplayValue],
  );
  // Dormant: the region-circle level was removed (the map now opens on all
  // countries). Kept for potential reuse; not called anywhere.
  // eslint-disable-next-line no-unused-vars
  const showRegions = useCallback(
    (list = filteredRegions) => {
      const maps = mapsRef.current;
      const map = mapInstanceRef.current;
      if (!maps || !map) return;
      closeAdmin1InfoOverlay();
      clearMovementLayer();
      resetAdmin1CircleColors();
      clearOverlays(overlaysRef.current.countries);
      clearOverlays(overlaysRef.current.admin1);
      clearOverlays(overlaysRef.current.admin2);
      clearOverlays(overlaysRef.current.regions);
      overlaysRef.current.countries = [];
      overlaysRef.current.admin1 = [];
      overlaysRef.current.admin2 = [];
      admin1CircleRefs.current.clear();
      admin1LabelRefs.current.clear();
      overlaysRef.current.regions = createRegionOverlays(maps, map, list);
      activeLabelsRef.current = overlaysRef.current.regions.filter(
        (overlay) => overlay.isLabel,
      );
      // Keep the loader up until the regional figures have actually loaded, so
      // the initial paint doesn't flash regions with no data. Once the
      // aggregate arrives this callback re-runs (filteredRegions changes) and
      // the loader clears.
      if (regionsToUse?.length) {
        finishMapLoading();
      }
    },
    [clearMovementLayer, closeAdmin1InfoOverlay, createRegionOverlays, filteredRegions, finishMapLoading, regionsToUse, resetAdmin1CircleColors],
  );
  const showCountries = useCallback(
    (list = allCountries) => {
      const maps = mapsRef.current;
      const map = mapInstanceRef.current;
      if (!maps || !map) return;
      closeAdmin1InfoOverlay();
      clearMovementLayer();
      resetAdmin1CircleColors();
      clearOverlays(overlaysRef.current.regions);
      clearOverlays(overlaysRef.current.admin1);
      clearOverlays(overlaysRef.current.admin2);
      clearOverlays(overlaysRef.current.countries);
      overlaysRef.current.regions = [];
      overlaysRef.current.admin1 = [];
      overlaysRef.current.admin2 = [];
      admin1CircleRefs.current.clear();
      admin1LabelRefs.current.clear();
      overlaysRef.current.countries = createCountryOverlays(maps, map, list);
      // Country labels are hover-only (like admin1/admin2), so keep them out of
      // the declutter set that force-shows labels on idle.
      activeLabelsRef.current = [];
      // At the top (all-countries) level, keep the loader up until the raw
      // country data has loaded so the initial paint doesn't flash an empty map.
      // Gate on the raw source (allCountriesList), NOT the filtered result — a
      // reason/year filter that legitimately yields zero countries must still
      // clear the loader (show an empty map) instead of spinning forever. When
      // drilling (an explicit list is passed) always clear it.
      if (list !== allCountries || allCountriesList?.length) {
        finishMapLoading();
      }
    },
    [allCountries, allCountriesList, clearMovementLayer, closeAdmin1InfoOverlay, createCountryOverlays, finishMapLoading, resetAdmin1CircleColors],
  );
  const showAdmin1 = useCallback(
    (list = admin1WithCoords) => {
      const maps = mapsRef.current;
      const map = mapInstanceRef.current;
      if (!maps || !map) return;
      closeAdmin1InfoOverlay();
      clearMovementLayer();
      resetAdmin1CircleColors();
      clearOverlays(overlaysRef.current.regions);
      clearOverlays(overlaysRef.current.countries);
      clearOverlays(overlaysRef.current.admin1);
      clearOverlays(overlaysRef.current.admin2);
      admin1CircleRefs.current.clear();
      admin1LabelRefs.current.clear();
      overlaysRef.current.regions = [];
      overlaysRef.current.countries = [];
      overlaysRef.current.admin2 = [];
      overlaysRef.current.admin1 = createAdmin1Overlays(maps, map, list);
      activeLabelsRef.current = [];
      finishMapLoading();
    },
    [admin1WithCoords, clearMovementLayer, closeAdmin1InfoOverlay, createAdmin1Overlays, finishMapLoading, resetAdmin1CircleColors],
  );
  const showAdmin2 = useCallback(
    (list = admin2WithCoords) => {
      const maps = mapsRef.current;
      const map = mapInstanceRef.current;
      if (!maps || !map) return;
      closeAdmin1InfoOverlay();
      clearMovementLayer();
      resetAdmin1CircleColors();
      clearOverlays(overlaysRef.current.regions);
      clearOverlays(overlaysRef.current.countries);
      clearOverlays(overlaysRef.current.admin1);
      clearOverlays(overlaysRef.current.admin2);
      admin1CircleRefs.current.clear();
      admin1LabelRefs.current.clear();
      overlaysRef.current.regions = [];
      overlaysRef.current.countries = [];
      overlaysRef.current.admin1 = [];
      overlaysRef.current.admin2 = createAdmin2Overlays(maps, map, list);
      activeLabelsRef.current = [];
      finishMapLoading();
    },
    [admin2WithCoords, clearMovementLayer, closeAdmin1InfoOverlay, createAdmin2Overlays, finishMapLoading, resetAdmin1CircleColors],
  );

  useEffect(() => {
    if (selectedCountry || selectedAdmin1) {
      return;
    }
    showCountries();
  }, [selectedCountry, selectedAdmin1, selectedCause, showCountries]);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then((maps) => {
        if (!isMounted || !mapRef.current) return;
        mapsRef.current = maps;
        mapInstanceRef.current = new maps.Map(mapRef.current, {
          zoom: DEFAULT_ZOOM,
          center: DEFAULT_CENTER,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: true,
          zoomControlOptions: {
            position: maps.ControlPosition.TOP_LEFT,
          },
          panControl: false,
          streetViewControl: false,
          gestureHandling: "cooperative",
          scrollwheel: false,
          isFractionalZoomEnabled: true,
        });
        const styledMap = new maps.StyledMapType(MAP_STYLE, { name: "Map" });
        mapInstanceRef.current.mapTypes.set("map", styledMap);
        mapInstanceRef.current.setMapTypeId("map");
        mapInstanceRef.current.addListener("idle", () => {
          resizeCircles();
          declutterLabels(activeLabelsRef.current);
        });
        // Resize during the zoom animation too, so circles keep a constant
        // pixel size instead of ballooning until the map settles (idle).
        mapInstanceRef.current.addListener("zoom_changed", () => {
          resizeCircles();
        });
        setMapStatus("ready");
      })
      .catch((error) => {
        if (!isMounted) return;
        setMapStatus(error.message === "missing-key" ? "missing-key" : "error");
      });

    return () => {
      isMounted = false;
      clearOverlays(overlaysRef.current.countries);
      clearOverlays(overlaysRef.current.admin1);
      clearOverlays(overlaysRef.current.admin2);
      clearOverlays(overlaysRef.current.regions);
      movementPolylinesRef.current.forEach((polyline) => polyline.setMap(null));
      movementPolylinesRef.current = [];
      movementContributionCirclesRef.current.forEach((circle) => circle.setMap(null));
      movementContributionCirclesRef.current = [];
      admin1CircleRefs.current.clear();
      admin1LabelRefs.current.clear();
      admin1PolygonRefs.current.clear();
      admin1InfoOverlayRef.current?.setMap?.(null);
      admin1InfoOverlayRef.current = null;
      boundaryPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
      boundaryPolygonsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (mapStatus === "ready" && !selectedCountry && !selectedAdmin1) {
      showCountries();
    }
  }, [mapStatus, selectedCountry, selectedAdmin1, showCountries]);

  // At the top (all-countries) level, reset the map to the global view. Country
  // and admin drill-downs are framed by their boundary fitBounds instead.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (selectedCountry || selectedAdmin1) return;
    map.panTo(DEFAULT_CENTER);
    map.setZoom(DEFAULT_ZOOM);
    showCountries();
  }, [selectedCountry, selectedAdmin1, showCountries]);

  useEffect(() => {
    if (!selectedCountry || selectedAdmin1) return;
    if (movementAdmin1) return;
    if (mapStatus !== "ready") return;
    showAdmin1();
  }, [
    admin1WithCoords,
    mapStatus,
    movementAdmin1,
    selectedAdmin1,
    selectedCountry,
    showAdmin1,
  ]);

  useEffect(() => {
    if (!selectedAdmin1) return;
    if (mapStatus !== "ready") return;
    showAdmin2();
  }, [admin2WithCoords, mapStatus, selectedAdmin1, showAdmin2]);

  return (
    <>
      <div className={`${classes.map_timeline}${mapLoading ? ` ${classes.map_timeline_disabled}` : ""}`} aria-label="Timeline">
        <div className={classes.map_timeline_track}>
          {TIMELINE_YEARS.map((year) => (
            <span
              key={year}
              className={`${classes.map_timeline_year}${year === selectedYear ? ` ${classes.map_timeline_year_active}` : ""}${mapLoading ? ` ${classes.map_timeline_year_disabled}` : ""}`}
              onClick={() => selectyear(year)}
              aria-disabled={mapLoading}
            >
              {year}
            </span>
          ))}
        </div>
      </div>
      <div className={classes.map_box}>
        <div id="homemap" ref={mapRef} className={classes.map_canvas} />
        {mapToast && (
          <div className={classes.map_toast} role="status">
            {mapToast}
          </div>
        )}
        {mapStatus !== "ready" && (
          <div className={classes.map_notice}>
            {mapStatus === "missing-key"
              ? "Add REACT_APP_GOOGLE_MAPS_API_KEY to load the map."
              : mapStatus === "error"
                ? "Map failed to load."
                : "Loading map..."}
          </div>
        )}
        {mapLoading && (
          <div className={classes.map_loader} role="status" aria-live="polite">
            <span className={classes.map_loader_spinner} aria-hidden="true" />
            <span>Loading map data...</span>
          </div>
        )}
        {showEmptyState && (
          <div className={classes.map_empty} role="status" aria-live="polite">
            <div className={classes.map_empty_card}>
              <svg
                className={classes.map_empty_icon}
                viewBox="0 0 24 24"
                width="40"
                height="40"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z" />
                <line x1="3" y1="3" x2="21" y2="21" />
              </svg>
              <p className={classes.map_empty_message}>{noDataMessage}</p>
            </div>
          </div>
        )}
        {(activeRegionId || selectedCountry || selectedAdmin1) && (
        <div className={classes.map_toolbar}>
        <div
          className={classes.population_toggle}
          role="group"
          aria-label="Population metric"
        >
          {[
            { key: "total", label: "Total" },
            { key: "male", label: "Male" },
            { key: "female", label: "Female" },
          ].map((option) => {
            // Disable male/female when the current level has no such breakdown.
            // The active option is never disabled (so it can't look broken).
            const noData =
              (option.key === "male" && !hasMaleData) ||
              (option.key === "female" && !hasFemaleData);
            const disabled = noData && populationType !== option.key;
            return (
              <button
                key={option.key}
                type="button"
                className={`${classes.population_toggle_button}${
                  populationType === option.key
                    ? ` ${classes.population_toggle_button_active}`
                    : ""
                }${disabled ? ` ${classes.population_toggle_button_disabled}` : ""}`}
                aria-pressed={populationType === option.key}
                disabled={disabled}
                title={disabled ? "No data at this level" : undefined}
                onClick={() =>
                  dispatch(selectionsSliceActions.setPopulationType(option.key))
                }
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {(selectedCountry || selectedAdmin1) && (
          <div className={classes.map_filter_stack}>
            <div className={classes.map_filter_bar}>
              <span className={classes.map_operation_label}>
                Operation context
              </span>
              <select
                className={classes.map_filter_select}
                value={selectedOperation}
                onChange={(event) => {
                  clearIdpMovementMode();
                  dispatch(selectionsSliceActions.setRoundFilter(null));
                  dispatch(selectionsSliceActions.setOperation(event.target.value));
                }}
              >
                {operationOptions.map((operation) => {
                  const disabled = isOperationDisabled(operation);
                  return (
                    <option key={operation} value={operation} disabled={disabled}>
                      {operation}
                      {disabled ? " (no data)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            {movementAdmin1 && (
              <div className={classes.movement_filter_bar}>
                <span className={classes.map_operation_label}>
                  IDP movement
                </span>
                <div
                  className={classes.movement_direction_pill}
                  role="group"
                  aria-label={`IDP movement direction for ${movementAdmin1.name}`}
                >
                  {[
                    { key: "arrivals", label: "Arrivals" },
                    { key: "departing", label: "Departing" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`${classes.movement_direction_button}${
                        movementDirection === option.key
                          ? ` ${classes.movement_direction_button_active}`
                          : ""
                      }`}
                      aria-pressed={movementDirection === option.key}
                      onClick={() => {
                        setMovementDirection(option.key);
                        dispatch(selectionsSliceActions.setIdpMovementDirection(option.key));
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
        )}
        {movementHoverDetail && (
          <div className={classes.movement_detail_card} role="status">
            <span className={classes.movement_detail_label}>
              {movementHoverDetail.isInternal
                ? "Internal movement"
                : movementHoverDetail.direction === "departing"
                ? "Departing movement"
                : "Arrival movement"}
            </span>
            <div className={classes.movement_detail_route}>
              <span>{movementHoverDetail.from}</span>
              <span aria-hidden="true">→</span>
              <span>{movementHoverDetail.to}</span>
            </div>
            <div className={classes.movement_detail_value}>
              {formatFullValue(movementHoverDetail.value)}
              <span>IDPs</span>
            </div>
          </div>
        )}
        {showLegend && (
        <div
          className={classes.map_legend}
          aria-label={`${legendTitle} legend`}
        >
          <span className={classes.map_legend_label}>{legendTitle}</span>
          <div className={classes.map_legend_bubbles}>
            {(() => {
              const maxLegendValue = Math.max(
                0,
                ...legendValues.filter(Number.isFinite),
              );
              return legendValues.map((value, index) => {
                const diameter =
                  2 * getSymbolRadiusPx(value, maxLegendValue, legendMinValue);
                return (
                  <div
                    key={`${value}-${index}`}
                    className={classes.map_legend_item}
                  >
                    <span
                      className={classes.map_legend_circle}
                      style={{
                        width: diameter,
                        height: diameter,
                        background: legendColor,
                      }}
                    />
                    <span className={classes.map_legend_value}>
                      {formatValue(value)}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
        )}

        {selectedCountry ? (
            <div className={`${classes.info_panel}${infoCollapsed ? ` ${classes.info_panel_collapsed}` : ""}`}>
            <button
              type="button"
              className={`${classes.info_collapse}${infoCollapsed ? ` ${classes.info_collapse_collapsed}` : ""}`}
              onClick={() => setInfoCollapsed((value) => !value)}
              aria-expanded={!infoCollapsed}
              aria-label={infoCollapsed ? "Expand panel" : "Collapse panel"}
              title={infoCollapsed ? "Expand" : "Collapse"}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {infoCollapsed && (
              <button
                type="button"
                className={classes.info_back_collapsed}
                onClick={() => backToRegions()}
                aria-label={selectedAdmin1 ? "Back to admin 1" : "Back to countries"}
                title={selectedAdmin1 ? "Back to admin 1" : "Back to countries"}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            <div className={classes.info_title}>
              {selectedAdmin2 || selectedAdmin1 || selectedCountry}
            </div>
            {!infoCollapsed && (
            <>
            <div className={classes.info_subtitle}>
              Displacement figures
            </div>
            <div className={classes.info_meta}>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Cause</span>
                <span className={classes.info_meta_value}>{selectedCause || "All"}</span>
              </div>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Year</span>
                <span className={classes.info_meta_value}>{selectedYear}</span>
              </div>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Operation</span>
                <span className={classes.info_meta_value}>
                  {selectedOperation && selectedOperation !== "All" ? selectedOperation : "All"}
                </span>
              </div>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Level</span>
                <span className={classes.info_meta_value}>
                  {selectedAdmin1 ? "Admin 2" : selectedCountry ? "Admin 1" : "Regional"}
                </span>
              </div>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Population</span>
                <span className={classes.info_meta_value}>
                  {populationType === "male"
                    ? "Male"
                    : populationType === "female"
                      ? "Female"
                      : "Total"}
                </span>
              </div>
            </div>
            {/* <ul className={classes.info_list}>
                        {activeRegion.countries.map((country) => (
                            <li key={country.id} className={classes.info_item}>
                                <span>{country.name}</span>
                                <span>{formatValue(getItemValue(country))}</span>
                            </li>
                        ))}
                    </ul> */}
            <div
              className={`${classes.info_actions}${
                selectedCountry ? "" : ` ${classes.info_actions_centered}`
              }`}
            >
              <button
                type="button"
                className={classes.reset_button}
                onClick={() => backToRegions()}
              >
                {selectedAdmin1
                  ? "Back to admin 1"
                  : selectedCountry
                    ? "Back to countries"
                    : "Back to regions"}
              </button>
              {selectedCountry && (
                <button
                  type="button"
                  className={classes.get_data_button}
                  onClick={() => setApiUrlModalOpen(true)}
                >
                  Get data
                </button>
              )}
            </div>
            </>
            )}
          </div>
        ) : (
          <div className={`${classes.info_panel} ${classes.info_panel_intro}${infoCollapsed ? ` ${classes.info_panel_collapsed}` : ""}`}>
            <button
              type="button"
              className={`${classes.info_collapse}${infoCollapsed ? ` ${classes.info_collapse_collapsed}` : ""}`}
              onClick={() => setInfoCollapsed((value) => !value)}
              aria-expanded={!infoCollapsed}
              aria-label={infoCollapsed ? "Expand panel" : "Collapse panel"}
              title={infoCollapsed ? "Expand" : "Collapse"}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className={classes.info_title}>
              Global displacement overview
            </div>
            {!infoCollapsed && (
            <>
            <div className={classes.info_subtitle}>
              Internally displaced population tracked by DTM,<br />
              aggregated by country.
            </div>
            <div className={classes.info_meta}>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Cause</span>
                <span className={classes.info_meta_value}>{selectedCause || "All"}</span>
              </div>
              <div className={classes.info_meta_row}>
                <span className={classes.info_meta_label}>Year</span>
                <span className={classes.info_meta_value}>{selectedYear}</span>
              </div>
            </div>
            {hasData && (
              <div className={classes.info_hint}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 3l7.5 18 2.4-7.1L20 11.5 3 3z" />
                </svg>
                <span>Click any circle to explore country figures</span>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>      
      <ApiConfigModal
        open={apiUrlModalOpen}
        url={dtmApiUrl}
        onClose={() => setApiUrlModalOpen(false)}
      />
    </>
  );
};

export default MapView;
